'use client';
import { useEffect, useMemo, useState } from 'react';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/style.css';
import {
  Cake, Wine, CalendarHeart, Wheat, Shell, Check, ArrowLeft,
  Egg, Nut, Bean, Flower2, Fish, Droplets,
  WheatOff, NutOff, MilkOff, Salad, FishOff, Leaf,
  Delete, Minus, Plus, UtensilsCrossed, CalendarCheck, ChevronLeft, ChevronRight,
  DoorClosed, CalendarDays, Pencil, Users, X, ChefHat, Clock3, AlertTriangle, Moon, BedDouble, ArrowRight, BookOpen, FileText, LogIn, LogOut, Sparkles, ConciergeBell, CalendarX2, Info, Hash, Wrench, Heart, MapPin, Sunrise, Waves,
} from 'lucide-react';
import { t, setTranslations } from './i18n';

const toISO = d => d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}` : '';

// "YYYY-MM-DD HH:mm:ss" → ISO date + full time, e.g. "2026-08-25 · 19:48:32"
const fmtStamp = (s) => {
  if (!s) return '';
  const [date, time] = s.split(' ');
  return `${date} · ${time ?? ''}`.trim();
};

const OCCASIONS = [
  { id: 'birthday', label: 'Birthday', icon: Cake },
  { id: 'honeymoon', label: 'Honey Moon', icon: Wine },
  { id: 'anniversary', label: 'Anniversary', icon: CalendarHeart },
];
const ALLERGIES = ['eggs', 'peanuts', 'soya', 'sesame', 'wheat', 'fish', 'mustard'];
const DIETARY = ['gluten_free', 'nut_free', 'lactose_free', 'vegetarian', 'no_seafood', 'vegan'];
const ALLERGY_ICONS = { eggs: Egg, peanuts: Nut, soya: Bean, sesame: Flower2, wheat: Wheat, fish: Fish, mustard: Droplets };
const DIETARY_ICONS = { gluten_free: WheatOff, nut_free: NutOff, lactose_free: MilkOff, vegetarian: Salad, no_seafood: FishOff, vegan: Leaf };
const pretty = s => s.replaceAll('_', ' ');

// Celebratory floating-hearts + starfield decoration, shared by every honeymoon page.
// The parent <main> must carry `hm-decor` (relative + isolate) so this paints behind content.
function HmPetals() {
  return (
    <div className="hm-petals" aria-hidden="true">
      <span className="hm-stars" />
      {[...Array(20)].map((_, i) => (
        <span key={i} className="hm-petal" style={{ '--i': i }}>
          <Heart size={12 + (i % 4) * 3} strokeWidth={0} fill="currentColor" />
        </span>
      ))}
    </div>
  );
}

// CSS art per outlet until real photos are uploaded — clean tonal tints
const CARD_ART = {
  reefs: 'linear-gradient(150deg, #e0f2fe 0%, #bae6fd 100%)',
  cedar: 'linear-gradient(150deg, #fff7ed 0%, #fed7aa 100%)',
  matsu: 'linear-gradient(150deg, #f5f3ff 0%, #ddd6fe 100%)',
  sicilia: 'linear-gradient(150deg, #f7fee7 0%, #d9f99d 100%)',
  masala: 'linear-gradient(150deg, #fffbeb 0%, #fde68a 100%)',
};

// Brand accent per restaurant — sampled from each menu PDF's own design.
const BRAND = {
  reefs:   '#304878', // deep navy blue (Reefs & Shells seafood)
  cedar:   '#901830', // Lebanese deep red
  matsu:   '#7a5a2e', // warm bronze (Asian)
  masala:  '#a84818', // Indian burnt orange
  sicilia: '#486030', // Italian olive green
};
// Soft brand-tint backdrop behind each menu cover (sampled from the PDF page).
const COVER_BG = {
  reefs: '#f0f8e2', cedar: '#d9d2ba', matsu: '#ebd7c5', masala: '#e8eadb', sicilia: '#e1ddb4',
};

const FLAG_FILE = { en: 'gb', fr: 'fr', de: 'de', ru: 'ru', ar: 'eg', it: 'it', cs: 'cz', pl: 'pl' };

// Meal-period label shown under each time slot ("seat"). Opera timing names come
// through in English (DINNER / LUNCH / BREAKFAST); translate them for every kiosk
// language so the Arabic version doesn't show a Latin "DINNER", etc. Unknown names
// (custom area labels) are shown as-is.
const MEAL_I18N = {
  DINNER:    { en: 'Dinner',    ar: 'عشاء', fr: 'Dîner',          de: 'Abendessen',  it: 'Cena',      ru: 'Ужин',    cs: 'Večeře',  pl: 'Kolacja' },
  LUNCH:     { en: 'Lunch',     ar: 'غداء', fr: 'Déjeuner',       de: 'Mittagessen', it: 'Pranzo',    ru: 'Обед',    cs: 'Oběd',    pl: 'Obiad' },
  BREAKFAST: { en: 'Breakfast', ar: 'فطور', fr: 'Petit-déjeuner', de: 'Frühstück',   it: 'Colazione', ru: 'Завтрак', cs: 'Snídaně', pl: 'Śniadanie' },
};
const mealLabel = (name, lang) => {
  const base = (name || '').replace(/\s+\d+$/, '').trim();
  if (!base) return '';
  const m = MEAL_I18N[base.toUpperCase()];
  return m ? (m[lang] ?? m.en) : base;
};

// Guest nationality → kiosk language, limited to the languages the kiosk offers.
// Opera sends full country names (see stays.nationality); ISO/short codes accepted
// too. Anything unmapped → no auto switch (guest keeps English / their own choice).
const NAT_LANG = {
  russia: 'ru', russian: 'ru', ru: 'ru', rus: 'ru',
  germany: 'de', german: 'de', de: 'de', deu: 'de', austria: 'de', austrian: 'de', at: 'de', switzerland: 'de', swiss: 'de',
  france: 'fr', french: 'fr', fr: 'fr', fra: 'fr', belgium: 'fr',
  italy: 'it', italian: 'it', it: 'it', ita: 'it',
  'czech republic': 'cs', czechia: 'cs', czech: 'cs', cz: 'cs', cze: 'cs', slovakia: 'cs', slovak: 'cs',
  poland: 'pl', polish: 'pl', pl: 'pl', pol: 'pl',
  egypt: 'ar', egyptian: 'ar', eg: 'ar', 'saudi arabia': 'ar', saudi: 'ar', bahrain: 'ar', kuwait: 'ar', qatar: 'ar',
  'united arab emirates': 'ar', uae: 'ar', oman: 'ar', jordan: 'ar', jordanian: 'ar', lebanon: 'ar', lebanese: 'ar',
  morocco: 'ar', moroccan: 'ar', algeria: 'ar', algerian: 'ar', tunisia: 'ar', libya: 'ar', iraq: 'ar', syria: 'ar',
};
const natToLang = nat => { const k = String(nat ?? '').trim().toLowerCase(); return k ? (NAT_LANG[k] ?? null) : null; };

// API error → translated guest message. Codes map to err_* keys; {param} slots
// are filled from the response's params object. Unknown codes fall back to the
// API's English text, then to the generic fallback key.
const ERR_KEY = {
  INVALID_ROOM: 'err_invalid_room', NO_STAY: 'err_no_stay',
  DEPARTURE_REQUIRED: 'err_departure_required', MISMATCH: 'err_mismatch',
  PAST_DATE: 'err_past_date', OUTSIDE_STAY: 'err_outside_stay',
  MAINTENANCE: 'err_maintenance', OUTSIDE_WORK_TIME: 'err_work_time',
  NO_SHARED: 'err_no_shared', COMPANION_NOT_FOUND: 'err_companion_not_found',
  COMPANION_MISMATCH: 'err_companion_mismatch', LAST_DAY: 'err_last_day',
  SAME_DAY_CLOSED: 'err_same_day_closed', SAME_DAY_LIMIT: 'err_same_day_limit',
  SLOT_CLOSED: 'err_slot_closed', SLOT_FULL: 'err_slot_full',
  HM_NOT_ELIGIBLE: 'err_hm_not_eligible', HM_ALREADY: 'err_hm_already', HM_DISABLED: 'err_hm_disabled',
  HM_SLOT_FULL: 'err_hm_slot_full', HM_CLOSED_DATE: 'err_hm_closed_date', RATE_LIMITED: 'err_rate_limited',
  DISABLED: 'err_disabled', CANCEL_DISABLED: 'err_cancel_disabled',
  CANCEL_CLOSED: 'err_cancel_closed', CANCEL_NOT_FOUND: 'err_cancel_not_found',
  MIN_STAY: 'err_min_stay', NO_RULE: 'err_no_rule',
};
// Guest-fixable issues get the softer amber treatment; the identity ones also
// carry a hint on where to find the right details.
const WARN_CODES = new Set([
  'INVALID_ROOM', 'NO_STAY', 'DEPARTURE_REQUIRED', 'MISMATCH', 'PAST_DATE',
  'OUTSIDE_STAY', 'COMPANION_NOT_FOUND', 'COMPANION_MISMATCH', 'LAST_DAY',
  'SAME_DAY_CLOSED', 'SAME_DAY_LIMIT', 'SLOT_CLOSED', 'SLOT_FULL',
  'MIN_STAY', 'QUOTA_USED', 'NO_SHARED', 'CANCEL_DISABLED', 'CANCEL_CLOSED',
]);
// Warnings that really are about the guest's own entered details → "Check Your Details".
// All other warnings are availability/timing/rule issues → a neutral "couldn't complete" title.
const IDENTITY_WARN = new Set([
  'INVALID_ROOM', 'NO_STAY', 'DEPARTURE_REQUIRED', 'MISMATCH', 'OUTSIDE_STAY',
  'COMPANION_NOT_FOUND', 'COMPANION_MISMATCH',
]);
// Resolve "N word(suffix)" plural markers left in translations, e.g. "1 reservation(s)"
// → "1 reservation", "2 reservation(s)" → "2 reservations", "1 Reservierung(en)" → "1 Reservierung".
const pluralize = msg => String(msg).replace(/(\d+)(\s+)([^\s()]+)\(([^)]+)\)/g,
  (_, n, sp, word, suf) => `${n}${sp}${word}${Number(n) === 1 ? '' : suf}`);

function apiError(lang, data, fallbackKey = 'server_error') {
  const warn = WARN_CODES.has(data?.code);
  let key = data?.code === 'QUOTA_USED'
    ? (Number(data.params?.days) > 0 ? 'err_quota_window' : 'err_quota')
    : ERR_KEY[data?.code];
  if (!key) return { msg: data?.error ?? t(lang, fallbackKey), warn: false };
  let msg = t(lang, key);
  const AR_D = ['٠','١','٢','٣','٤','٥','٦','٧','٨','٩'];
  const dig = v => lang === 'ar' ? String(v ?? '').replace(/[0-9]/g, d => AR_D[+d]) : String(v ?? '');
  for (const [k, v] of Object.entries(data.params ?? {})) msg = msg.replaceAll(`{${k}}`, dig(v));
  const titleKey = warn ? (IDENTITY_WARN.has(data?.code) ? 'warn_title' : 'notice_title') : 'error_title';
  return { msg: pluralize(msg), warn, titleKey };
}

// Explicit MSA Arabic weekday names (browser locales vary / return colloquial forms)
const AR_WEEKDAYS_LONG = ['الأحد', 'الاثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت'];
const AR_WEEKDAYS_NARROW = ['ح', 'ن', 'ث', 'ر', 'خ', 'ج', 'س'];
const AR_WEEKDAYS_SHORT = ['أحد', 'اثنين', 'ثلاثاء', 'أربعاء', 'خميس', 'جمعة', 'سبت'];
const AR_MONTHS = ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو', 'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'];

const fmtDay = (d, lang) => ({
  iso: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`,
  dow: lang === 'ar' ? AR_WEEKDAYS_LONG[d.getDay()] : d.toLocaleDateString(lang, { weekday: 'short' }),
  dom: d.getDate(),
  mon: lang === 'ar' ? AR_MONTHS[d.getMonth()] : d.toLocaleDateString(lang, { month: 'short' }),
});

// Category icons — matched by keyword in the category name (English), generic fallback.
const CAT_ICONS = [
  [/salad/i, Salad], [/soup|broth|potage/i, UtensilsCrossed],
  [/dessert|sweet|cake|pastr|ice ?cream/i, Cake],
  [/drink|beverage|wine|juice|cocktail|coffee|tea|water|soft/i, Wine],
  [/fish|seafood|shell|shrimp|prawn/i, Fish],
  [/veg|vegan|green/i, Leaf], [/appetiz|starter|mezze|meze|hot|cold/i, ChefHat],
];
const catIcon = name => (CAT_ICONS.find(([re]) => re.test(name || '')) || [])[1] || UtensilsCrossed;

export default function Kiosk() {
  const [step, setStep] = useState('home');
  const [myRoom, setMyRoom] = useState('');
  const [myDep, setMyDep] = useState('');
  const [myCalOpen, setMyCalOpen] = useState(false);
  const [myBookings, setMyBookings] = useState(null);
  const [myGuest, setMyGuest] = useState('');
  const [myAccomp, setMyAccomp] = useState([]);
  const [cancelBk, setCancelBk] = useState(null); // booking pending cancel confirmation
  const [restaurants, setRestaurants] = useState([]);
  const [restaurant, setRestaurant] = useState(null);
  const [days, setDays] = useState([]);
  const [datesReady, setDatesReady] = useState(false); // true once availability is computed (prevents empty-state flash)
  const [idleWarn, setIdleWarn] = useState(0); // seconds left before idle auto-reset; 0 = warning hidden
  // Honeymoon Experience (VIP-gated module)
  const [hmData, setHmData] = useState(null);   // GET /api/honeymoon payload (menus, slots, redeemed)
  const [hmMeal, setHmMeal] = useState(null);   // 'dinner' | 'breakfast'
  const [hmDate, setHmDate] = useState('');     // chosen date (ISO)
  const [hmSlot, setHmSlot] = useState(null);   // chosen slot id
  const [hmResult, setHmResult] = useState(null); // booking confirmation
  const [hmCal, setHmCal] = useState(false);      // honeymoon date picker open
  const [hmVenue, setHmVenue] = useState(null);   // chosen honeymoon venue (restaurant code)
  const [hmGroup, setHmGroup] = useState(null);   // chosen breakfast menu group (Romance / Oriental)
  const [hmChoices, setHmChoices] = useState({}); // { dishId: [selected option labels] } for optioned dishes
  // Staff bypass: an admin can log in to enter the kiosk while it is in maintenance.
  const [maintBypass, setMaintBypass] = useState(false);
  const [staffOpen, setStaffOpen] = useState(false);
  const [staffUser, setStaffUser] = useState('');
  const [staffPass, setStaffPass] = useState('');
  const [staffErr, setStaffErr] = useState('');
  const [staffBusy, setStaffBusy] = useState(false);
  const [slotsByDay, setSlotsByDay] = useState({});
  const [when, setWhen] = useState(null); // {date, time, ...day}
  const [pax, setPax] = useState(2);
  const [occasion, setOccasion] = useState(null);
  const [dietOpen, setDietOpen] = useState(null); // 'allergies' | 'dietary'
  const [allergies, setAllergies] = useState([]);
  const [dietary, setDietary] = useState([]);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [room, setRoom] = useState('');
  const [departure, setDeparture] = useState('');
  const [stay, setStay] = useState(null);
  const [calOpen, setCalOpen] = useState(false);
  const [settings, setSettings] = useState(null);
  const [quote, setQuote] = useState(null);
  const [menu, setMenu] = useState(null); // categories with items
  const [menuPdfs, setMenuPdfs] = useState([]); // printable per-language PDFs
  const [pdfOpen, setPdfOpen] = useState(null); // url currently viewed
  const [appSettings, setAppSettings] = useState(null);
  const [booted, setBooted] = useState(false); // hold the UI until config is loaded (no flash)
  const [companions, setCompanions] = useState([]); // shared-table rooms
  const [compRoom, setCompRoom] = useState('');
  const [compDep, setCompDep] = useState('');
  const [compCalOpen, setCompCalOpen] = useState(false);
  const [compMode, setCompMode] = useState(false);
  const [dishes, setDishes] = useState({}); // itemId -> qty
  const [lang, setLang] = useState('en');
  const [langChosen, setLangChosen] = useState(false); // did the guest pick a language themselves?
  const [langOpen, setLangOpen] = useState(false); // compact switcher expanded?
  const [langs, setLangs] = useState([]);
  // The honeymoon menu is localized server-side at fetch time, so re-fetch it when the
  // guest switches language (otherwise dishes/sections/options stay in the old language).
  useEffect(() => {
    if (!hmData || !room) return;
    let alive = true;
    fetch(`/api/honeymoon?room=${room}&departure=${departure}&lang=${lang}`)
      .then(r => (r.ok ? r.json() : null))
      .then(data => {
        if (!alive || !data?.menus) return;
        setHmData(data);
        setHmGroup(g => (g ? (data.menus[hmMeal]?.variants?.find(v => v.variant === g.variant) || null) : g));
      })
      .catch(() => {});
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);
  // The admin-configured default language (admin → Languages → Set Default); English if none set.
  const defaultLang = useMemo(() => langs.find(l => l.is_default)?.code || 'en', [langs]);
  const [, setI18nReady] = useState(0); // re-render once backend translations load

  useEffect(() => {
    // Config loaders (small payloads). Refreshed in the background so anything an
    // admin changes reaches the kiosk without a reload. None of these override the
    // guest's current language mid-session.
    const loadRestaurants = () => fetch('/api/restaurants').then(r => r.json()).then(setRestaurants).catch(() => {});
    const loadLangs = () => fetch('/api/languages').then(r => r.json()).then(setLangs).catch(() => {});
    const loadTrans = () => fetch('/api/translations').then(r => r.json()).then(d => { setTranslations(d); setI18nReady(v => v + 1); }).catch(() => {});
    const loadApp = () => fetch('/api/app-settings').then(r => r.json()).then(setAppSettings).catch(() => {});
    // Initial load — and adopt the admin-configured default language exactly once.
    loadRestaurants();
    const pLangs = fetch('/api/languages').then(r => r.json()).then(ls => {
      setLangs(ls);
      const def = ls.find(l => l.is_default)?.code;
      if (def) setLang(def);
    }).catch(() => {});
    const pTrans = loadTrans();
    const pApp = loadApp();
    // Reveal the UI only once language + translations + settings are in (or after a 3s
    // safety timeout), so the FIRST paint is already in the right language and translated —
    // no flash of English/untranslated content and no wrong maintenance state.
    let shown = false;
    const reveal = () => { if (!shown) { shown = true; setBooted(true); } };
    Promise.allSettled([pLangs, pTrans, pApp]).then(reveal);
    const safety = setTimeout(reveal, 3000);
    // Background refresh every 10s so admin changes (hide/show, settings, translations) reach the kiosk promptly.
    const iv = setInterval(() => { loadRestaurants(); loadLangs(); loadTrans(); loadApp(); }, 10000);
    return () => { clearInterval(iv); clearTimeout(safety); };
  }, []);

  // Default the departure date to today (client-only to avoid hydration mismatch);
  // the guest can still change it from the calendar.
  useEffect(() => {
    const today = toISO(new Date());
    setDeparture(d => d || today);
    setMyDep(d => d || today);
  }, []);

  // auto-dismiss the error popup if the guest walks away
  useEffect(() => {
    if (!error) return;
    const tm = setTimeout(() => setError(''), 15000);
    return () => clearTimeout(tm);
  }, [error]);

  useEffect(() => {
    // Honor the language's direction (admin → Languages → Direction). RTL languages
    // (e.g. Arabic) flip the layout to right-to-left; everything else stays LTR.
    document.documentElement.dir = (langs.find(l => l.code === lang)?.rtl ? 'rtl' : 'ltr');
    document.documentElement.lang = lang;
  }, [lang, langs]);

  // Kiosk hardening: make right-click / middle-click completely inert (no context
  // menu, no button press-animation, no handler) and block image dragging.
  useEffect(() => {
    const noCtx = e => e.preventDefault();
    const noDrag = e => e.preventDefault();
    // Swallow non-primary buttons at the capture phase, before they reach any element,
    // so a right-click can't trigger :active states or click handlers.
    const noAux = e => { if (e.button !== 0) { e.preventDefault(); e.stopPropagation(); } };
    document.addEventListener('contextmenu', noCtx, true);
    document.addEventListener('pointerdown', noAux, true);
    document.addEventListener('mousedown', noAux, true);
    document.addEventListener('mouseup', noAux, true);
    document.addEventListener('dragstart', noDrag);
    return () => {
      document.removeEventListener('contextmenu', noCtx, true);
      document.removeEventListener('pointerdown', noAux, true);
      document.removeEventListener('mousedown', noAux, true);
      document.removeEventListener('mouseup', noAux, true);
      document.removeEventListener('dragstart', noDrag);
    };
  }, []);

  // Kiosk idle timeout: if a guest walks away mid-flow, return to the home screen
  // after a period of inactivity so the next guest starts fresh. Duration is admin-
  // configurable via app setting `idle_seconds` (default 90s); 0 disables it.
  useEffect(() => {
    if (step === 'home') { setIdleWarn(0); return; }
    const secs = appSettings?.idle_seconds !== undefined ? Number(appSettings.idle_seconds) : 90;
    if (!Number.isFinite(secs) || secs <= 0) return;
    // Show an "Are you still there?" warning with a live countdown for the last
    // `lead` seconds, so a guest reading a long menu isn't silently reset. Any
    // interaction (or the "I'm here" button, which triggers a click) dismisses it.
    const lead = Math.min(15, Math.max(5, Math.floor(secs / 3)));
    let resetT, warnT, tickT;
    const clearAll = () => { clearTimeout(resetT); clearTimeout(warnT); clearInterval(tickT); };
    const kick = () => {
      clearAll(); setIdleWarn(0);
      warnT = setTimeout(() => {
        let left = lead; setIdleWarn(left);
        tickT = setInterval(() => { left -= 1; setIdleWarn(left > 0 ? left : 0); }, 1000);
        resetT = setTimeout(() => { clearAll(); setIdleWarn(0); reset(); }, lead * 1000);
      }, (secs - lead) * 1000);
    };
    const events = ['pointerdown', 'keydown', 'touchstart', 'click', 'scroll'];
    kick();
    for (const ev of events) window.addEventListener(ev, kick, { passive: true });
    return () => { clearAll(); for (const ev of events) window.removeEventListener(ev, kick); };
  }, [step, appSettings]);

  // Pull fresh config at the start of every guest session (each landing on the home
  // screen — after idle-reset or "Done"), so a new guest always sees the latest admin
  // changes instantly, not up to a poll-interval later. Never overrides the language.
  useEffect(() => {
    if (step !== 'home') return;
    fetch('/api/restaurants').then(r => r.json()).then(setRestaurants).catch(() => {});
    fetch('/api/languages').then(r => r.json()).then(setLangs).catch(() => {});
    fetch('/api/translations').then(r => r.json()).then(d => { setTranslations(d); setI18nReady(v => v + 1); }).catch(() => {});
    fetch('/api/app-settings').then(r => r.json()).then(setAppSettings).catch(() => {});
  }, [step]);

  // Close any open date picker when the guest taps outside it (or its trigger).
  useEffect(() => {
    if (!calOpen && !compCalOpen && !myCalOpen) return;
    const onDown = e => {
      if (e.target.closest('.calwrap') || e.target.closest('.id2box') || e.target.closest('.idbox')) return;
      setCalOpen(false); setCompCalOpen(false); setMyCalOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [calOpen, compCalOpen, myCalOpen]);

  // Physical keyboard support for the room-number keypads (digits + Backspace).
  // Targets whichever room field is active for the current step.
  useEffect(() => {
    const setter = step === 'myroom' ? setMyRoom
      : (step === 'welcome' && compMode) ? setCompRoom
      : step === 'identity' ? setRoom
      : null;
    if (!setter) return;
    const onKey = e => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      if (/^[0-9]$/.test(e.key)) { e.preventDefault(); setter(r => (r + e.key).slice(0, 6)); }
      else if (e.key === 'Backspace') { e.preventDefault(); setter(r => r.slice(0, -1)); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [step, compMode]);

  // Keep the OPEN restaurant in sync with the 10s list poll, so admin edits
  // (description, photos, logo, cover) appear live without leaving the screen.
  // Only replaces the snapshot when its data actually changed — no churn.
  useEffect(() => {
    if (!restaurant) return;
    const fresh = restaurants.find(r => r.code === restaurant.code);
    if (fresh && JSON.stringify(fresh) !== JSON.stringify(restaurant)) setRestaurant(fresh);
  }, [restaurants]); // eslint-disable-line react-hooks/exhaustive-deps

  // Per-restaurant settings drive how many dates the guest sees and where they
  // start. Keyed on code and polled every 10s so admin setting changes apply
  // live; updates state only on real change to avoid re-render flicker.
  useEffect(() => {
    if (!restaurant) return;
    setSettings(null);
    let cancelled = false;
    const load = () => fetch(`/api/settings/${restaurant.code}`).then(r => r.json())
      .then(s => { if (!cancelled) setSettings(prev => (prev && JSON.stringify(prev) === JSON.stringify(s)) ? prev : s); })
      .catch(() => { if (!cancelled) setSettings(prev => prev ?? {}); });
    load();
    const iv = setInterval(load, 10000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [restaurant?.code]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!settings) { setDatesReady(false); return; }
    const count = Math.max(1, Number(settings.dates_shown ?? 3));
    let offset = settings.start_from_today === 'no' ? 1 : 0;
    const t = new Date();
    // If same-day booking would start today but today's cutoff has already passed,
    // don't offer today — start from tomorrow instead.
    if (offset === 0) {
      const m = /^(\d{1,2}):(\d{2})$/.exec(settings.same_day_close_time || '');
      if (m) {
        const cutoff = Number(m[1]) * 60 + Number(m[2]);
        if (t.getHours() * 60 + t.getMinutes() >= cutoff) offset = 1;
      }
    }
    // "N days in advance": look at the next `count` CALENDAR days from the start
    // offset, and show only the bookable ones — hiding closed weekdays, the
    // departure day (when not reservable), and anything past the guest's stay.
    // Closed days are NOT skipped over to reach a later open day.
    const openDays = restaurant?.op_days ? restaurant.op_days.split(',').map(Number) : null;
    const depTime = stay?.departure ? new Date(stay.departure + 'T00:00:00').getTime() : null;
    const noLastDay = settings.last_day_reserve === 'no'; // departure day not reservable
    const out = [];
    for (let i = 0; i < count; i++) {
      const d = new Date(t.getFullYear(), t.getMonth(), t.getDate() + offset + i);
      if (depTime != null) {
        if (d.getTime() > depTime) continue;                     // past departure — hide
        if (noLastDay && d.getTime() === depTime) continue;      // departure day — not reservable
      }
      if (openDays && !openDays.includes(d.getDay())) continue;   // closed day — hide
      out.push(fmtDay(d, lang));
    }
    setDays(out);
    setDatesReady(true); // availability is now computed — safe to show empty-state
  }, [settings, lang, restaurant, stay]);

  useEffect(() => {
    if (!restaurant) { setMenu(null); return; }
    fetch(`/api/restaurants/${restaurant.code}/menu?lang=${lang}`)
      .then(r => r.ok ? r.json() : [])
      .then(setMenu)
      .catch(() => setMenu([]));
  }, [restaurant, lang]);

  useEffect(() => {
    if (!restaurant) { setMenuPdfs([]); return; }
    fetch(`/api/restaurants/${restaurant.code}/menu-pdfs`)
      .then(r => r.ok ? r.json() : [])
      .then(setMenuPdfs)
      .catch(() => setMenuPdfs([]));
  }, [restaurant]);

  // price quote — drives the extra-charge warning (on the date step and the details step)
  useEffect(() => {
    const date = step === 'details' ? when?.date : step === 'when' ? days[0]?.iso : null;
    if (!date || !restaurant || !room || !departure) { setQuote(null); return; }
    // On the details step we know the chosen timing — pass it so a "paid" slot is
    // priced the same way the booking will be (quote/create parity).
    const slot = step === 'details' && when?.time ? `&time=${when.time}&timingId=${when.timingId}` : '';
    fetch(`/api/reservations/quote?room=${room}&departure=${departure}&restaurant=${restaurant.code}&date=${date}${slot}`)
      .then(r => r.ok ? r.json() : null)
      .then(setQuote)
      .catch(() => setQuote(null));
  }, [step, restaurant, when, days, room, departure]);

  useEffect(() => {
    if (!restaurant || !days.length) return;
    let cancelled = false;
    const fetchSlots = () => Promise.all(
      days.map(d =>
        fetch(`/api/availability?restaurant=${restaurant.code}&date=${d.iso}`)
          .then(r => (r.ok ? r.json() : null))
          .then(slots => [d.iso, slots])
          .catch(() => [d.iso, null])
      )
    ).then(entries => {
      if (cancelled) return;
      // keep the previous day's slots when a poll fails (API restart, blip)
      setSlotsByDay(prev => {
        const next = { ...prev };
        for (const [iso, slots] of entries) if (slots) next[iso] = slots;
        return next;
      });
    });
    fetchSlots();
    const iv = setInterval(fetchSlots, 8000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [restaurant, days]);

  const toggle = (list, setList, v) =>
    setList(list.includes(v) ? list.filter(x => x !== v) : [...list, v]);

  // Guest-facing error popup — replaces inline error text on every step
  const eobj = !error ? null : typeof error === 'string' ? { msg: error, warn: false } : error;
  const errorModal = (
    <>
      {eobj ? (
        <div className="errmask" onClick={() => setError('')}>
          <div className={'errpop' + (eobj.warn ? ' warn' : ' err')} onClick={e => e.stopPropagation()}>
            <div className="erricon">
              <span className="erricon-halo" aria-hidden="true" />
              {eobj.warn ? <AlertTriangle size={34} strokeWidth={1.75} /> : <X size={34} strokeWidth={1.75} />}
            </div>
            <div className="errtitle">{t(lang, eobj.titleKey ?? (eobj.warn ? 'warn_title' : 'error_title'))}</div>
            <div className="errmsg">{eobj.msg}</div>
            <button className="errok" onClick={() => setError('')}>{t(lang, 'ok')}</button>
          </div>
        </div>
      ) : null}
      {idleWarn > 0 ? (
        <div className="errmask" onClick={() => setIdleWarn(0)}>
          <div className="errpop warn" onClick={e => e.stopPropagation()}>
            <div className="erricon">
              <span className="erricon-halo" aria-hidden="true" />
              <Clock3 size={34} strokeWidth={1.75} />
            </div>
            <div className="errtitle">{t(lang, 'still_there')}</div>
            <div className="errmsg" style={{ fontSize: 42, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{nd(idleWarn)}</div>
            <button className="errok" onClick={() => setIdleWarn(0)}>{t(lang, 'im_here')}</button>
          </div>
        </div>
      ) : null}
    </>
  );

  // Compact in-flow language switcher. Shown on the branded screens once the guest
  // is past the home page so they can change language at any step (e.g. after they
  // have entered their room + departure). Picking here counts as a manual choice.
  const flagImg = code => (
    <img src={`/img/flags/${FLAG_FILE[code] ?? code}.svg`} alt=""
      loading="eager" decoding="async"
      ref={node => { if (node && node.complete) node.classList.add('ready'); }}
      onLoad={e => e.currentTarget.classList.add('ready')}
      onError={e => e.currentTarget.classList.add('ready')} />
  );
  const langBar = (
    <div className={'langbar' + (langOpen ? ' open' : '')} role="group" aria-label={t(lang, 'language')}>
      {langOpen && (
        <div className="langflyout" onClick={e => e.stopPropagation()}>
          {langs.map(l => (
            <button key={l.code} className={'langflag' + (lang === l.code ? ' sel' : '')}
              aria-label={l.native_name || l.name} title={l.native_name || l.name}
              onClick={() => { setLang(l.code); setLangChosen(true); setLangOpen(false); }}>
              {flagImg(l.code)}
            </button>
          ))}
        </div>
      )}
      <button className="langtoggle" aria-label={t(lang, 'language')} aria-expanded={langOpen}
        onClick={() => setLangOpen(o => !o)}>
        {flagImg(lang)}
      </button>
      {langOpen && <div className="langmask" onClick={() => setLangOpen(false)} />}
    </div>
  );

  // Format the chosen date's month in the CURRENT language, from the stored ISO
  // date — so switching language on the review/details screen reformats it too
  // (the month was previously baked in whatever language was active at selection).
  const monOf = (iso) => iso ? new Date(iso + 'T00:00:00').toLocaleDateString(lang, { month: 'short' }) : '';
  const domOf = (iso) => iso ? new Date(iso + 'T00:00:00').getDate() : '';

  // Arabic-Indic digits (٠–٩) when the language is Arabic; Western otherwise.
  // Wrap any guest-facing number with nd() so dates, times, prices, quantities
  // and pax all read fully in Arabic.
  const AR_DIGITS = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩'];
  const nd = (v) => lang === 'ar' ? String(v ?? '').replace(/[0-9]/g, d => AR_DIGITS[+d]) : String(v ?? '');
  // Arabic-Indic (or Western) digits → Western digits only
  const denum = (s) => String(s ?? '').replace(/[٠-٩]/g, d => AR_DIGITS.indexOf(d)).replace(/\D/g, '');
  // Pax with correct Arabic number agreement after the preposition "لـ":
  // 1 → فرد, 2 → فردين (genitive dual), 3–10 → N أفراد, 11+ → N فرداً.
  // Other languages: plain (localized) number.
  const paxUnit = (n) => {
    if (lang !== 'ar') return nd(n);
    const x = Number(n);
    if (x === 1) return 'فرد';
    if (x === 2) return 'فردين';
    if (x >= 3 && x <= 10) return `${nd(x)} أفراد`;
    return `${nd(x)} فرداً`;
  };
  // Localize the currency label: show Egyptian Pound as "ج.م" in Arabic instead of "L.E"
  const curOf = (c) => (lang === 'ar' && /^\s*(l\.?e\.?|egp|le)\s*$/i.test(c || '')) ? 'ج.م' : (c || '');
  // "Items" label for the running count in the menu bar
  const itemsLbl = ({ en: 'Items', ar: 'الأصناف', fr: 'Articles', de: 'Artikel', ru: 'Позиции', it: 'Articoli', cs: 'Položky', pl: 'Pozycje' })[lang] ?? 'Items';
  // Restaurant logo shown across the booking flow — admin-controlled via restaurants.logo_url,
  // falling back to the bundled brand logo at /img/logos/{code}.png.
  const restLogo = restaurant ? (restaurant.logo_url || `/img/logos/${restaurant.code}.png`) : '';
  // Date/time separator: a localized "at" word — clearer than a symbol and avoids the middot merging with Arabic-Indic zeros
  const atWord = ({ en: 'at', ar: 'الساعة', fr: 'à', de: 'um', ru: 'в', it: 'alle', cs: 'v', pl: 'o' })[lang] ?? 'at';
  const dtSep = <span className="dtsep"><span className="dtpipe" aria-hidden="true" />{atWord}</span>;

  // Once the guest is identified, switch to the language for their nationality —
  // but only if they never picked one themselves at the start.
  const maybeAutoLang = (nationality) => {
    if (langChosen) return;
    const code = natToLang(nationality);
    if (code && code !== lang && langs.some(l => l.code === code)) setLang(code);
  };

  async function verifyGuest() {
    setBusy(true);
    setError('');
    try {
      const res = await fetch(`/api/stays/${room}?departure=${departure}`);
      const data = await res.json();
      if (!res.ok) { setError(apiError(lang, data)); return; }
      setStay(data);
      // Default the party size to the guest's ACTUAL count from their stay
      // (Opera adults + children) instead of a fixed number.
      setPax(Math.max(1, Number(data.adults ?? 0) + Number(data.children ?? 0)));
      maybeAutoLang(data.nationality);
      setStep('welcome');
    } catch {
      setError(t(lang, 'server_error'));
    } finally {
      setBusy(false);
    }
  }

  // Is the current guest eligible for the honeymoon module? (client-side gate for
  // showing the card; the server re-checks authoritatively on every call.)
  const honeymoonEligible = () => {
    if (appSettings?.honeymoon_enabled !== 'yes' || !stay?.VIP_STATUS) return false;
    const allowed = String(appSettings.honeymoon_vip || 'V4').split(',').map(s => s.trim().toUpperCase()).filter(Boolean);
    return allowed.includes(String(stay.VIP_STATUS).toUpperCase());
  };

  // Load the honeymoon config/menus/redemptions and open the module.
  async function openHoneymoon() {
    setBusy(true); setError('');
    try {
      const res = await fetch(`/api/honeymoon?room=${room}&departure=${departure}&lang=${lang}`);
      const data = await res.json();
      if (!res.ok) { setError(apiError(lang, data)); return; }
      setHmData(data); setHmMeal(null); setHmDate(''); setHmSlot(null); setHmResult(null); setHmGroup(null); setHmChoices({});
      setStep('honeymoon');
    } catch { setError(t(lang, 'server_error')); } finally { setBusy(false); }
  }

  // Book one honeymoon meal.
  async function hmBook() {
    if (!hmMeal || hmSlot == null || !hmDate) return;
    setBusy(true); setError('');
    // Gather the couple's per-dish option picks from the menu they'll actually eat.
    const activeVariant = (hmMeal === 'breakfast' && hmGroup) ? hmGroup : (hmData?.menus?.[hmMeal]?.variants?.[0]);
    const dishes = (activeVariant?.sections || []).flatMap(s => s.dishes || []);
    const grpShown = (dc, grp) => !grp.showWhen || Object.values(dc || {}).some(arr => arr.some(o => o.en === grp.showWhen));
    const choices = dishes.map(d => {
      const dc = hmChoices[d.id]; if (!dc) return null;
      const picks = [];
      // Re-resolve each pick against the CURRENT menu (by stable English key) so a
      // mid-flow language switch can't send stale/mixed-language option labels.
      (d.optionGroups || []).forEach((grp, gi) => { if (grpShown(dc, grp)) (dc[gi] || []).forEach(o => picks.push((grp.opts || []).find(x => x.en === o.en) || o)); });
      return picks.length ? { dish: d.dish, picks } : null;
    }).filter(Boolean);
    try {
      const res = await fetch('/api/honeymoon/book', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        // Send the CANONICAL variant (not the localized name) so the stored booking is language-stable.
        body: JSON.stringify({ room, departure, meal: hmMeal, date: hmDate, slot_id: hmSlot, restaurant: hmVenue, variant: hmGroup?.variant || '', choices }),
      });
      const data = await res.json();
      if (!res.ok) { setError(apiError(lang, data)); return; }
      setHmResult(data);
      setHmData(d => d ? ({ ...d, redeemed: { ...d.redeemed, [hmMeal]: { meal: hmMeal, date: data.date, time: data.time, label: data.label, ref: data.ref } } }) : d);
      setStep('hmdone');
    } catch { setError(t(lang, 'server_error')); } finally { setBusy(false); }
  }

  // Staff bypass: a valid admin login lets staff into the kiosk during maintenance.
  async function staffLogin() {
    if (!staffUser || !staffPass) return;
    setStaffBusy(true); setStaffErr('');
    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: staffUser, password: staffPass }),
      });
      const d = await res.json().catch(() => ({}));
      if (res.ok && d.token) {
        // Only a Super Administrator may bypass maintenance — reject any other role.
        if ((d.user?.roles || []).includes('super_admin')) {
          setMaintBypass(true); setStaffOpen(false); setStaffUser(''); setStaffPass('');
        } else {
          setStaffErr('Administrator access only');
        }
      } else setStaffErr(d.error || 'Invalid username or password');
    } catch { setStaffErr('Cannot reach the server'); }
    finally { setStaffBusy(false); }
  }

  async function reserve() {
    setBusy(true);
    setError('');
    try {
      const res = await fetch('/api/reservations', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          room, departure, restaurant: restaurant.code, date: when.date, time: when.time,
          timingId: when.timingId, pax, occasion, allergies, dietary,
          menuItems: Object.entries(dishes).filter(([, q]) => q > 0).map(([id, qty]) => ({ id: Number(id), qty })),
          companions: companions.map(c => ({ room: c.room, departure: c.departure })),
        }),
      });
      const data = await res.json();
      if (!res.ok) { setError(apiError(lang, data)); return; }
      setResult(data);
      setStep('done');
    } catch {
      setError(t(lang, 'server_error'));
    } finally {
      setBusy(false);
    }
  }

  async function loadMyBookings() {
    setBusy(true);
    setError('');
    try {
      const res = await fetch(`/api/reservations?room=${myRoom}&departure=${myDep}`);
      const data = await res.json();
      if (!res.ok) { setError(apiError(lang, data)); return; }
      setMyGuest(data.guest || '');
      setMyAccomp(data.accompany || []);
      setMyBookings(data.bookings || []);
      maybeAutoLang(data.nationality);
      setStep('mylist');
    } catch {
      setError(t(lang, 'server_error'));
    } finally {
      setBusy(false);
    }
  }

  async function cancelBooking(id) {
    setError('');
    const res = await fetch(`/api/reservations/${id}/cancel`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ room: myRoom, departure: myDep }),
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      setError(apiError(lang, data));
      return;
    }
    await loadMyBookings();
  }

  function reset() {
    const today = toISO(new Date());
    setStep('home'); setRestaurant(null); setWhen(null); setPax(2);
    setMyRoom(''); setMyBookings(null); setMyGuest(''); setMyAccomp([]); setMyDep(today); setMyCalOpen(false);
    setOccasion(null); setAllergies([]); setDietary([]); setDietOpen(null);
    setError(''); setResult(null); setRoom(''); setDeparture(today); setStay(null);
    setDishes({}); setMenu(null); setCompanions([]); setCompRoom(''); setCompMode(false);
    setLang(defaultLang); setLangChosen(false); // fresh session: back to the configured default language
  }

  // Hold a clean loading screen until config (settings + language + translations) is in,
  // so the first real paint is fully styled, in the correct language and translated —
  // eliminating load flashes on refresh / first access.
  if (!booted) return <main className="maint" />;

  if (appSettings?.maintenance_mode === 'yes' && !maintBypass) return (
    <main className="maint">
      {langs.length > 1 && langBar}
      <div className="maintcard">
        <div className="mainticon"><Wrench size={42} strokeWidth={1.5} /></div>
        <h1>{t(lang, 'maintenance_title')}</h1>
        <p>{t(lang, 'maintenance_note')}</p>
        <div className="maintbar"><span /></div>
      </div>
      <div className="maintfooter">
        <div className="maintqrbox">
          <img className="maintqr" src="/img/wa-qr.svg" alt={t(lang, 'report_issue')} width={128} height={128} />
          <span className="maintqrcap">{t(lang, 'report_issue')}</span>
        </div>
        <div className="maintbrand">
          <div className="maintbrandname">El Alamy Kiosk</div>
          <div className="maintby">Made with <span className="maintheart">♥</span> by Mahmoud ElMoataz</div>
        </div>
        <a className="maintwa" href="https://wa.me/201008051616" target="_blank" rel="noreferrer">
          <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden="true"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
          <span dir="ltr">+20 100 805 1616</span>
        </a>
      </div>
      <button className="maintstaff" onClick={() => { setStaffOpen(true); setStaffErr(''); }}>Root Access</button>
      {staffOpen && (
        <div className="maintstaffmask" onClick={() => setStaffOpen(false)}>
          <div className="maintstaffbox" onClick={e => e.stopPropagation()}>
            <div className="maintstafftitle">Root Access</div>
            <input className="maintstaffin" placeholder="Username" autoFocus autoComplete="off"
              value={staffUser} onChange={e => setStaffUser(e.target.value)} />
            <input className="maintstaffin" type="password" placeholder="Password" autoComplete="off"
              value={staffPass} onChange={e => setStaffPass(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') staffLogin(); }} />
            {staffErr && <div className="maintstafferr">{staffErr}</div>}
            <div className="maintstaffbtns">
              <button onClick={() => setStaffOpen(false)}>Cancel</button>
              <button className="prim" disabled={staffBusy} onClick={staffLogin}>{staffBusy ? '…' : 'Enter Kiosk'}</button>
            </div>
          </div>
        </div>
      )}
    </main>
  );

  if (step === 'home') return (
    <main className="home">
      <div className="flags">
        {langs.map(l => (
          <button key={l.code} className={'flag' + (lang === l.code ? ' sel' : '')}
            aria-label={l.native_name || l.name} onClick={() => { setLang(l.code); setLangChosen(true); }}>
            <img src={`/img/flags/${FLAG_FILE[l.code] ?? l.code}.svg`} alt={l.native_name || l.name}
              loading="eager" decoding="async"
              ref={node => { if (node && node.complete) node.classList.add('ready'); }}
              onLoad={e => e.currentTarget.classList.add('ready')}
              onError={e => e.currentTarget.classList.add('ready')} />
          </button>
        ))}
      </div>
      <div className="halves">
        <button type="button" className="half book" onClick={() => setStep('restaurants')}>
          <div className="halftitle">
            <span className="halficon"><UtensilsCrossed size={36} strokeWidth={1.5} /></span>
            <span className="dash" />{t(lang, 'book_restaurant')}
          </div>
        </button>
        {appSettings?.my_bookings_enabled !== 'no' && (
          <button type="button" className="half mybook" onClick={() => { setError(''); setStep('myroom'); }}>
            <div className="halftitle">
              <span className="halficon"><CalendarCheck size={36} strokeWidth={1.5} /></span>
              <span className="dash" />{t(lang, 'my_bookings')}
            </div>
          </button>
        )}
      </div>
    </main>
  );

  if (step === 'myroom') {
    return (
      <main className="idpage2" style={{ backgroundImage: 'radial-gradient(120% 120% at 18% 0%, #1c2a44 0%, #0b1120 55%, #080b14 100%)' }}>
        <div className="id2left">
          <h1 className="id2title">{t(lang, 'my_bookings')}</h1>

          <div className="id2fields">
            <div className="id2field">
              <div className="id2label">{t(lang, 'room_number')}</div>
              <input className="id2box" inputMode="numeric" autoComplete="off"
                value={nd(myRoom)} dir="ltr"
                onChange={e => setMyRoom(denum(e.target.value).slice(0, 6))} />
            </div>

            {appSettings?.departure_validation !== 'no' && (
              <div className="id2field">
                <div className="id2label">{t(lang, 'departure_date')}</div>
                <button className="id2box asbtn" onClick={() => setMyCalOpen(o => !o)}>
                  {myDep ? <span className="isonum">{nd(myDep)}</span> : t(lang, 'select_date')}
                </button>
                {myCalOpen && (
                  <div className="calwrap id2cal">
                    <DayPicker mode="single"
                      selected={myDep ? new Date(myDep + 'T00:00:00') : undefined}
                      onSelect={d => { setMyDep(toISO(d)); setMyCalOpen(false); }}
                      disabled={{ before: new Date() }}
                      formatters={{ formatWeekdayName: d => lang === 'ar' ? AR_WEEKDAYS_SHORT[d.getDay()] : d.toLocaleDateString(lang, { weekday: 'short' }), formatDay: d => nd(d.getDate()), formatCaption: d => `${lang === 'ar' ? AR_MONTHS[d.getMonth()] : d.toLocaleDateString(lang, { month: 'long' })} ${nd(d.getFullYear())}` }} />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="id2keypad">
          {['1','2','3','4','5','6','7','8','9'].map(k => (
            <button key={k} onClick={() => setMyRoom(r => (r + k).slice(0, 6))}>{nd(k)}</button>
          ))}
          <span />
          <button onClick={() => setMyRoom(r => (r + '0').slice(0, 6))}>{nd('0')}</button>
          <button onClick={() => setMyRoom(r => r.slice(0, -1))} aria-label={t(lang, 'backspace')}><ChevronLeft size={44} strokeWidth={2.75} /></button>
        </div>

        {!myCalOpen && <button className="ri2back" onClick={reset} aria-label={t(lang, 'back')}><ArrowLeft size={30} /></button>}
        <button className="ri2next ri2arrow" disabled={busy || !myRoom || (appSettings?.departure_validation !== 'no' && !myDep)} onClick={loadMyBookings}>
          {busy ? t(lang, 'checking') : t(lang, 'next')}
        </button>
        {errorModal}
      </main>
    );
  }

  if (step === 'mylist') {
    return (
      <main className="idpage2 mylistp" style={{ backgroundImage: 'radial-gradient(120% 120% at 18% 0%, #1c2a44 0%, #0b1120 55%, #080b14 100%)' }}>
        <div className="mylisthead">
          <h1 className="whentitle" style={{ position: 'static' }}>{t(lang, 'my_bookings')}</h1>
          {myGuest && <div className="mylisthi">{t(lang, 'hi')}, {myGuest}</div>}
          {myAccomp.length > 0 && (
            <div className="mylistparty">
              <Users size={17} />
              <span>{t(lang, 'and')} {myAccomp.join(' · ')}</span>
            </div>
          )}
        </div>
        {myBookings?.length === 0 && <div className="mylistempty">{t(lang, 'no_bookings')}</div>}
        <div className="mylist">
          {myBookings?.map(b => {
              const d = new Date(b.date + 'T00:00:00');
              const dow = d.toLocaleDateString(lang, { weekday: lang === 'ar' ? 'narrow' : 'short' });
              const mon = d.toLocaleDateString(lang, { month: 'short' });
              return (
            <div key={b.id} className={'mlrow' + (b.status === 'cancelled' ? ' off' : '')}>
              <div className="mldate">
                <span className="mldateDow">{dow}</span>
                <span className="mldateDay">{d.getDate()}</span>
                <span className="mldateMon">{mon}</span>
              </div>
              <div className="mlinfo">
                <div className="mltitlerow">
                  <span className="mltitle">{b.restaurant}</span>
                  {b.cuisine && <span className="mlcuisine">{b.cuisine}</span>}
                  <span className={'mlbadge ' + (b.status === 'confirmed' ? 'ok' : b.status === 'cancelled' ? 'cancelled' : 'muted')}>{t(lang, 'status_' + b.status)}</span>
                  {b.status !== 'cancelled' && b.charge_type === 'chargeable' && <span className="mlbadge charge">{t(lang, 'extra_charge')}</span>}
                </div>
                <div className="mlmeta">
                  <span><Clock3 size={15} /> {nd(b.time)}</span>
                  <span><Users size={15} /> {t(lang, 'table_for')} {paxUnit(b.pax)}</span>
                  <span className="mliso"><CalendarDays size={15} /> {nd(b.date)}</span>
                  {b.booked_at && <span className="mlstamp"><CalendarCheck size={14} /> {nd(fmtStamp(b.booked_at))}</span>}
                  {b.status === 'cancelled' && b.cancelled_at && <span className="mlstamp cancel"><X size={13} /> {t(lang, 'cancelled_on')} {nd(fmtStamp(b.cancelled_at))}</span>}
                </div>
              </div>
              {b.cancellable && (
                <button className="mlcancel" onClick={() => setCancelBk(b)}><CalendarX2 size={18} strokeWidth={2} /> {t(lang, 'cancel')}</button>
              )}
            </div>
              );
          })}
        </div>
        {langBar}
        {errorModal}
        {cancelBk && (
          <div className="errmask" onClick={() => setCancelBk(null)}>
            <div className="errpop" onClick={e => e.stopPropagation()}>
              <div className="erricon warn"><CalendarX2 size={34} strokeWidth={1.75} /></div>
              <div className="errtitle">{t(lang, 'cancel_q')}</div>
              <div className="errmsg">{cancelBk.restaurant} · {nd(cancelBk.date)} · {nd(cancelBk.time)}</div>
              <div className="confbtns">
                <button className="confkeep" onClick={() => setCancelBk(null)}>{t(lang, 'keep_it')}</button>
                <button className="confyes" onClick={async () => { const id = cancelBk.id; setCancelBk(null); await cancelBooking(id); }}>{t(lang, 'cancel_yes')}</button>
              </div>
            </div>
          </div>
        )}
        <button className="ri2back" onClick={reset} aria-label={t(lang, 'back')}><ArrowLeft size={30} /></button>
        <button className="ri2next" onClick={() => setStep('restaurants')}>{t(lang, 'new_booking')}</button>
      </main>
    );
  }

  if (step === 'restaurants') return (
    <main className="mosaic">
      <div className={'mosaicgrid n' + Math.min(restaurants.length, 6)}>
        {restaurants.map((r, i) => {
          const img = r.image_url;
          return (
            <button
              key={r.code}
              className={'mtile' + (i === 0 ? ' feature' : '')}
              style={img
                ? { backgroundImage: `linear-gradient(0deg, rgba(15,23,42,0.15) 0%, rgba(15,23,42,0) 32%), url(${img})` }
                : { backgroundImage: CARD_ART[r.code] ?? CARD_ART.cedar }}
              onClick={() => { setRestaurant(r); setError(''); setStep('intro'); }}
            >
              {!img && <div className="mtilename">{r.names?.[lang] ?? r.name}</div>}
              {r.chargeable && (
                <span className="mcharge">
                  {t(lang, 'extra_charge')}{r.charge_amount > 0 ? ` · ${nd(r.charge_amount)} ${curOf(r.currency)}`.trimEnd() : ''}
                </span>
              )}
            </button>
          );
        })}
      </div>
      <button className="mosaicback" onClick={reset} aria-label={t(lang, 'back')}><ArrowLeft size={28} /></button>
    </main>
  );

  if (step === 'welcome') {
    const arr = stay?.arrival ? new Date(stay.arrival + 'T00:00:00') : null;
    const dep = stay?.departure ? new Date(stay.departure + 'T00:00:00') : null;
    const nights = arr && dep ? Math.round((dep - arr) / 86400000) : null;
    const accomp = (stay?.accompany_names ?? '')
      .split(';').map(x => x.trim()).filter(Boolean)
      .map(x => { const [l, f] = x.split(', '); return f ? `${f} ${l}` : x; });
    const bg = settings?.kiosk_bg_url || restaurant.image_url || '/img/gallery/ambience-1.webp';
    const idBg = { '--brand': (BRAND[restaurant.code] ?? '#2563eb'), backgroundImage: bg ? `linear-gradient(rgba(10,13,20,0.86), rgba(10,13,20,0.93)), url(${bg})` : (CARD_ART[restaurant.code] ?? CARD_ART.cedar) };
    const canShare = settings?.connected_tables === 'yes';

    // Adding a companion room — full keypad view
    if (compMode) return (
      <main className="idpage2" style={idBg}>
        <img className="steplogo" src={restLogo} alt={restaurant.names?.[lang] ?? restaurant.name} onError={e => { e.currentTarget.style.display = 'none'; }} />
        <div className="id2left">
          <h1 className="id2title">{t(lang, 'shared_with')}</h1>

          <div className="id2fields">
          <div className="id2field">
            <div className="id2label">{t(lang, 'room_number')}</div>
            <input className="id2box" inputMode="numeric" autoComplete="off"
              value={nd(compRoom)} dir="ltr"
              onChange={e => setCompRoom(denum(e.target.value).slice(0, 6))} />
          </div>

          {appSettings?.departure_validation !== 'no' && (
            <div className="id2field">
              <div className="id2label">{t(lang, 'departure_date')}</div>
              <button className="id2box asbtn" onClick={() => setCompCalOpen(o => !o)}>
                {compDep ? <span className="isonum">{nd(compDep)}</span> : t(lang, 'select_date')}
              </button>
              {compCalOpen && (
                <div className="calwrap id2cal">
                  <DayPicker mode="single"
                    selected={compDep ? new Date(compDep + 'T00:00:00') : undefined}
                    onSelect={d => { setCompDep(toISO(d)); setCompCalOpen(false); }}
                    disabled={{ before: new Date() }}
                    formatters={{ formatWeekdayName: d => lang === 'ar' ? AR_WEEKDAYS_SHORT[d.getDay()] : d.toLocaleDateString(lang, { weekday: 'short' }), formatDay: d => nd(d.getDate()), formatCaption: d => `${lang === 'ar' ? AR_MONTHS[d.getMonth()] : d.toLocaleDateString(lang, { month: 'long' })} ${nd(d.getFullYear())}` }} />
                </div>
              )}
            </div>
          )}
          </div>
        </div>
        <div className="id2keypad">
          {['1','2','3','4','5','6','7','8','9'].map(k => (
            <button key={k} onClick={() => setCompRoom(r => (r + k).slice(0, 6))}>{nd(k)}</button>
          ))}
          <span />
          <button onClick={() => setCompRoom(r => (r + '0').slice(0, 6))}>{nd('0')}</button>
          <button onClick={() => setCompRoom(r => r.slice(0, -1))} aria-label={t(lang, 'backspace')}><ChevronLeft size={44} strokeWidth={2.75} /></button>
        </div>
        {!compCalOpen && <button className="ri2back" onClick={() => { setError(''); setCompMode(false); }} aria-label={t(lang, 'back')}><ArrowLeft size={30} /></button>}
        <button className="ri2next ri2arrow"
          disabled={busy || !compRoom || compRoom === room || companions.some(x => x.room === compRoom) || (appSettings?.departure_validation !== 'no' && !compDep)}
          onClick={async () => {
            setBusy(true); setError('');
            try {
              const cdep = appSettings?.departure_validation !== 'no' ? compDep : '';
              const r2 = await fetch(`/api/stays/${compRoom}${cdep ? `?departure=${cdep}` : ''}`);
              const j = await r2.json().catch(() => ({}));
              if (!r2.ok || !j.room_number) { setError(apiError(lang, j)); return; }
              setCompanions([...companions, { room: compRoom, departure: cdep, name: j.display_name || j.guest_name || '' }]); setCompMode(false);
            } finally { setBusy(false); }
          }}>{busy ? t(lang, 'checking') : t(lang, 'next')}</button>
        {langBar}
        {errorModal}
      </main>
    );

    return (
      <main className="idpage2 wlm" style={idBg}>
        <img className="steplogo" src={restLogo} alt={restaurant.names?.[lang] ?? restaurant.name} onError={e => { e.currentTarget.style.display = 'none'; }} />
        {/* LEFT — welcome */}
        <div className="wlmleft">
          <div className="wl2check">
            <ConciergeBell size={40} strokeWidth={1.5} />
            {[...Array(10)].map((_, i) => <span key={i} className="wl2spark" style={{ '--i': i }} />)}
          </div>
          <div className="wl2welcome">{t(lang, 'welcome')},</div>
          <h1 className="wl2name">{stay?.display_name || stay?.guest_name}</h1>
          {accomp.length > 0 && <div className="wl2accomp">{t(lang, 'and')} {accomp.join(' · ')}</div>}
          {honeymoonEligible() && (
            <button className="hmcard" onClick={openHoneymoon} disabled={busy}>
              <span className="hmcard-ico"><Heart size={24} strokeWidth={2} fill="currentColor" /></span>
              <span className="hmcard-body">
                <span className="hmcard-titlerow">
                  <span className="hmcard-title">{t(lang, 'hm_experience')}</span>
                  <span className="hmcard-badge">{t(lang, 'hm_complimentary')}</span>
                </span>
                <span className="hmcard-sub">{t(lang, 'hm_congrats')}</span>
              </span>
              <span className="hmcard-go"><ArrowRight size={20} strokeWidth={2.25} /></span>
            </button>
          )}
          <div className="wl2grid">
            <div className="wl2cell"><i><BedDouble size={22} strokeWidth={1.75} /></i><span>{t(lang, 'room_no')}</span><b>{nd(stay?.room_number)}</b></div>
            {arr && <div className="wl2cell"><i><LogIn size={22} strokeWidth={1.75} /></i><span>{t(lang, 'arrival')}</span><b dir="ltr">{nd(toISO(arr))}</b></div>}
            <div className="wl2cell"><i><LogOut size={22} strokeWidth={1.75} /></i><span>{t(lang, 'departure')}</span><b dir="ltr">{dep ? nd(toISO(dep)) : ''}</b></div>
            {nights != null && nights > 0 && <div className="wl2cell"><i><Moon size={22} strokeWidth={1.75} /></i><span>{t(lang, 'nights')}</span><b>{nd(nights)}</b></div>}
            {stay?.board_name && <div className="wl2cell"><i><UtensilsCrossed size={22} strokeWidth={1.75} /></i><span>{t(lang, 'board')}</span><b>{stay.board_code || stay.board_name}</b></div>}
            <div className="wl2cell"><i><Users size={22} strokeWidth={1.75} /></i><span>{t(lang, 'guests')}</span><b>{nd(Number(stay?.adults ?? 0) + Number(stay?.children ?? 0))}</b></div>
          </div>
        </div>

        {/* RIGHT — share table (or a simple Continue) */}
        <div className="wlmright">
          {!datesReady ? (
            <div className="wlmloading" aria-busy="true"><span className="wlmspin" /></div>
          ) : days.length === 0 ? (
            <div className="wlmnoavail">
              <div className="nadot"><CalendarX2 size={38} strokeWidth={1.5} /></div>
              <p>{t(lang, 'no_dates')}</p>
              <button className="ri2next" style={{ position: 'static' }} onClick={() => setStep('restaurants')}>{t(lang, 'back')}</button>
            </div>
          ) : canShare ? (
            <>
              <h2 className="id2askq">{t(lang, 'share_q')}</h2>
              {companions.length > 0 && (
                <div className="compcards">
                  {companions.map(c => (
                    <div key={c.room} className="compcard">
                      <div className="compcheck"><Check size={20} strokeWidth={2.5} /></div>
                      <div className="compinfo">
                        <div className="comproom">{t(lang, 'room')} {c.room}</div>
                        {c.name && <div className="compname">{c.name}</div>}
                      </div>
                      <button className="compremove" aria-label={t(lang, 'remove')} onClick={() => setCompanions(companions.filter(x => x.room !== c.room))}><X size={16} /></button>
                    </div>
                  ))}
                </div>
              )}
              <div className="id2askbtns">
                {companions.length < 3 && (
                  <button className="id2askbtn" onClick={() => { setError(''); setCompRoom(''); setCompDep(''); setCompCalOpen(false); setCompMode(true); }}>
                    {t(lang, 'yes_add_room')}
                  </button>
                )}
                <button className="id2askbtn" onClick={() => { setError(''); setStep('when'); }}>{t(lang, 'no_proceed')}</button>
              </div>
            </>
          ) : (
            <button className="ri2next" style={{ position: 'static' }} onClick={() => setStep('when')}>{t(lang, 'next')}</button>
          )}
        </div>

        <button className="ri2back" onClick={() => { setError(''); setStep('identity'); }} aria-label={t(lang, 'back')}><ArrowLeft size={30} /></button>
        {langBar}
        {errorModal}
      </main>
    );
  }


  // ===== Honeymoon Experience (VIP-gated module) =====
  const hmBg = { backgroundColor: '#2a1420', backgroundImage: 'linear-gradient(180deg, #3f2032 0%, #2e1524 55%, #281221 100%)' };
  const hmInfo = { dinner: { Icon: Moon, title: 'hm_dinner', sub: 'hm_dinner_sub' }, breakfast: { Icon: Sunrise, title: 'hm_breakfast', sub: 'hm_breakfast_sub' } };

  if (step === 'honeymoon') {
    const red = hmData?.redeemed || {};
    const accompNames = (stay?.accompany_names ?? '').split(';').map(x => x.trim()).filter(Boolean).map(x => { const [l, f] = x.split(', '); return f ? `${f} ${l}` : x; });
    const coupleNames = [stay?.display_name || stay?.guest_name, ...accompNames].filter(Boolean);
    // Warmer, personal: just the first names (strip title, keep the given name).
    const firstOf = (full) => (full || '').replace(/^(mr|mrs|ms|miss|dr|master|sir|madam)\.?\s+/i, '').trim().split(/\s+/)[0] || full;
    const coupleFirst = coupleNames.map(firstOf).filter(Boolean);
    return (
      <main className="idpage2 hmp hmmoments" style={hmBg}>
        <div className="hm-petals" aria-hidden="true">
          <span className="hm-stars" />
          {[...Array(20)].map((_, i) => <span key={i} className="hm-petal" style={{ '--i': i }}><Heart size={12 + (i % 4) * 3} strokeWidth={0} fill="currentColor" /></span>)}
        </div>
        <img className="hm-momentslogo" src="/uploads/hm/honeymoon-logo.png" alt={t(lang, 'hm_experience')} />
        <span className="hm-accent" aria-hidden="true" />
        {coupleNames.length > 0 && <div className="hm-greet">{t(lang, 'welcome')}</div>}
        {coupleNames.length > 0 && (
          <div className="hm-couple">
            {coupleNames.length === 2
              ? <><span className="hm-couple-name">{coupleNames[0]}</span><Heart className="hm-couple-heart" size={22} strokeWidth={0} fill="currentColor" /><span className="hm-couple-name">{coupleNames[1]}</span></>
              : coupleNames.map((n, i) => <span key={i} className="hm-couple-name">{n}</span>).reduce((a, b) => [a, ' · ', b])}
          </div>
        )}
        <p className="hm-welcome-msg">{t(lang, 'hm_welcome_msg')}</p>
        <div className="hm-momentcards">
          {['breakfast', 'dinner'].map(meal => {
            const info = hmInfo[meal]; const bk = red[meal];
            const loc = hmData?.locations?.[meal];
            const LocI = meal === 'dinner' ? Waves : ConciergeBell;
            return (
              <button key={meal} className={'hm-mcard' + (bk ? ' done' : '')} disabled={busy}
                onClick={() => {
                  // Fixed location per meal (beach / in-room) — no venue picker.
                  setHmMeal(meal); setHmSlot(null); setHmDate(toISO(new Date())); setHmCal(false); setHmVenue(null); setHmGroup(null); setHmChoices({});
                  if (bk) { setHmResult({ ...bk, meal }); setStep('hmdone'); return; }
                  setStep('hmenu');
                }}>
                <span className="hm-mcard-emblem">{info.Icon && <info.Icon size={38} strokeWidth={1.4} />}</span>
                <span className="hm-mcard-name">{t(lang, info.title)}</span>
                <span className="hm-mcard-desc">{t(lang, info.sub)}</span>
                {loc && <span className="hm-mcard-loc"><LocI size={15} strokeWidth={2} /> {loc}</span>}
                <span className="hm-mcard-status">{bk
                  ? <span className="hm-badge done">{t(lang, 'hm_reserved')} · <span className="isonum">{nd(bk.date)}</span> · <span className="isonum">{nd(bk.time)}</span></span>
                  : <span className="hm-badge ok">{t(lang, 'hm_available')}</span>}</span>
              </button>
            );
          })}
        </div>
        <button className="ri2back" onClick={() => setStep('welcome')} aria-label={t(lang, 'back')}><ArrowLeft size={30} /></button>
        {langBar}
        {errorModal}
      </main>
    );
  }

  if (step === 'hmvenue') {
    const venues = hmData?.venues || [];
    const info = hmInfo[hmMeal] || {};
    return (
      <main className="idpage2 hmp hm-decor" style={hmBg}>
        <HmPetals />
        <div className="hm-logo">{info.Icon && <info.Icon size={16} strokeWidth={1.9} className="hm-inlineico" />}{t(lang, hmMeal === 'dinner' ? 'hm_dinner' : 'hm_breakfast')}</div>
        <h1 className="hm-title">{t(lang, 'hm_choose_venue')}</h1>
        <span className="hm-accent" aria-hidden="true" />
        <div className="hm-tiles">
          {venues.map(v => (
            <button key={v.code} className="hm-tile" onClick={() => { setHmVenue(v.code); setStep('hmenu'); }}>
              <span className="hm-tile-ico">🍽</span>
              <span className="hm-tile-body"><span className="hm-tile-title">{v.names?.[lang] || v.name}</span></span>
              <span className="hm-go"><ChevronRight size={22} strokeWidth={2} /></span>
            </button>
          ))}
        </div>
        <button className="ri2back" onClick={() => setStep('honeymoon')} aria-label={t(lang, 'back')}><ArrowLeft size={30} /></button>
        {langBar}
        {errorModal}
      </main>
    );
  }

  if (step === 'hmenu') {
    const menu = hmData?.menus?.[hmMeal] || { pickOne: false, variants: [] };
    const info = hmInfo[hmMeal] || {};
    const backStep = 'honeymoon';
    const locNote = hmData?.locations?.[hmMeal] || '';
    const LocIcon = hmMeal === 'dinner' ? Waves : ConciergeBell;

    // BREAKFAST: pick which menu first (Romance / Oriental)
    if (menu.pickOne && !hmGroup) {
      return (
        <main className="idpage2 hmp hm-decor" style={hmBg}>
          <div className="hm-petals" aria-hidden="true">
            <span className="hm-stars" />
            {[...Array(20)].map((_, i) => <span key={i} className="hm-petal" style={{ '--i': i }}><Heart size={12 + (i % 4) * 3} strokeWidth={0} fill="currentColor" /></span>)}
          </div>
          <img className="hm-pagelogo" src="/uploads/hm/honeymoon-breakfast-logo.png" alt={t(lang, 'hm_breakfast')} />
          <h1 className="hm-title" style={{ fontStyle: 'normal', fontWeight: 600 }}>{t(lang, hmMeal === 'dinner' ? 'hm_choose_dinner' : 'hm_choose_breakfast')}</h1>
          <span className="hm-accent" aria-hidden="true" />
          {locNote && <div className="hm-locnote"><LocIcon size={16} strokeWidth={1.9} className="hm-inlineico" /> {locNote}</div>}
          <div className="hm-momentcards">
            {menu.variants.map(v => {
              const vlogo = ({ 'Romance Breakfast': 'romance', 'Oriental Breakfast': 'oriental' })[v.variant];
              return (
                <button key={v.variant} className="hm-mcard hm-vcard" onClick={() => { setHmChoices({}); setHmGroup(v); }}>
                  {vlogo
                    ? <img className="hm-vcard-logo" src={`/uploads/hm/logo-${vlogo}.png`} alt={v.name} />
                    : <span className="hm-mcard-name">{v.name}</span>}
                </button>
              );
            })}
          </div>
          <button className="ri2back" onClick={() => setStep(backStep)} aria-label={t(lang, 'back')}><ArrowLeft size={30} /></button>
          {langBar}
          {errorModal}
        </main>
      );
    }

    const activeVariant = menu.pickOne ? hmGroup : menu.variants[0];
    const sections = activeVariant?.sections || [];
    // Nested per-dish choices: hmChoices[dishId][groupIndex] = [selected opts]
    const pickOpt = (dishId, gi, opt, multi) => setHmChoices(prev => {
      const dc = { ...(prev[dishId] || {}) };
      const cur = dc[gi] || [];
      if (multi) { const has = cur.some(x => x.en === opt.en); dc[gi] = has ? cur.filter(x => x.en !== opt.en) : [...cur, opt]; }
      else dc[gi] = [opt];
      return { ...prev, [dishId]: dc };
    });
    // A conditional group (e.g. omelette fillings) shows only once its trigger is picked.
    const groupShown = (dc, grp) => !grp.showWhen || Object.values(dc || {}).some(arr => arr.some(o => o.en === grp.showWhen));
    const renderSection = (sec) => (
      <section key={sec.id} className="mcat">
        <h2 className="mcathd"><UtensilsCrossed className="mcaticon" size={18} strokeWidth={1.75} />{sec.name}</h2>
        <div className="mitems">
          {sec.dishes.map(dish => {
            const hasOpts = (dish.optionGroups || []).length > 0;
            const dc = hmChoices[dish.id] || {};
            return (
              <div key={dish.id} className={'mitem' + (hasOpts ? ' mitem-full' : '')}>
                {dish.photo
                  ? <img className="mi-photo" src={dish.photo} alt="" loading="lazy" onError={e => { e.currentTarget.style.visibility = 'hidden'; }} />
                  : <span className="mi-photo ph"><UtensilsCrossed size={20} strokeWidth={1.5} /></span>}
                <div className="mi-info">
                  <div className="mi-name">{dish.dish}</div>
                  {dish.description && <div className="mi-desc">{dish.description}</div>}
                  {(dish.optionGroups || []).map((grp, gi) => groupShown(dc, grp) && (
                    <div key={gi} className="hm-opts">
                      <div className="hm-optlabel">{grp.label || t(lang, grp.multi ? 'hm_choose_any' : 'hm_choose_one')}</div>
                      <div className="hm-optchips">
                        {grp.opts.map((opt, oi) => {
                          const sel = (dc[gi] || []).some(x => x.en === opt.en);
                          return <button key={oi} type="button" aria-pressed={sel} className={'hm-optchip' + (sel ? ' on' : '')} onClick={() => pickOpt(dish.id, gi, opt, grp.multi)}>{opt.label}</button>;
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </section>
    );
    // Balanced two-column packing: assign each section to the currently shorter
    // column (option sections weigh more since they render taller). Keeps each
    // column a tight vertical stack with no inter-row gaps.
    const colA = [], colB = []; let hA = 0, hB = 0;
    const secWeight = sec => 1 + (sec.dishes.some(d => (d.optionGroups || []).length) ? 2 : 0);
    sections.forEach(sec => { if (hA <= hB) { colA.push(sec); hA += secWeight(sec); } else { colB.push(sec); hB += secWeight(sec); } });
    // Every visible option group must have a selection before the guest can proceed.
    const allChosen = sections.every(sec => (sec.dishes || []).every(dish => {
      const dc = hmChoices[dish.id] || {};
      return (dish.optionGroups || []).every((grp, gi) => !groupShown(dc, grp) || (dc[gi] && dc[gi].length > 0));
    }));
    const menuBg = { ...hmBg, '--brand': '#e3b48c', '--hm-gold': '#e3b48c' };
    return (
      <main className="idpage2 menup hmmenu hm-decor" style={menuBg}>
        <HmPetals />
        <img className="steplogo" src={`/uploads/hm/logo-${menu.pickOne && hmGroup ? (({ 'Romance Breakfast': 'romance', 'Oriental Breakfast': 'oriental' })[hmGroup.variant]) : (hmMeal === 'dinner' ? 'dinner' : 'romance')}.png`} alt="" />
        <h1 className="whentitle">{t(lang, 'hm_how_you_like')}</h1>
        <div className="menuscroll">
          <div className="hm-cols">
            <div className="hm-col">{colA.map(renderSection)}</div>
            <div className="hm-col">{colB.map(renderSection)}</div>
          </div>
        </div>
        <button className="ri2back" onClick={() => { if (menu.pickOne) setHmGroup(null); else setStep(backStep); }} aria-label={t(lang, 'back')}><ArrowLeft size={30} /></button>
        <button className="ri2next ri2arrow" disabled={!allChosen} onClick={() => { setError(''); setStep('hwhen'); }}>{t(lang, 'next')}</button>
        {langBar}
        {errorModal}
      </main>
    );
  }

  if (step === 'hwhen') {
    const slots = hmData?.slots?.[hmMeal] || [];
    const taken = hmData?.taken || {};
    const info = hmInfo[hmMeal] || {};
    // A slot is open for a given day only if it isn't full (booked rooms < capacity)
    // and, for today, hasn't already passed.
    const now = new Date();
    const nowMin = now.getHours() * 60 + now.getMinutes();
    const todayIso = fmtDay(now, lang).iso;
    const slotOpen = (iso, s) => {
      if ((taken[`${hmMeal}|${iso}|${s.id}`] || 0) >= (s.seats || 0)) return false;
      if (iso === todayIso) { const [hh, mm] = String(s.time).split(':').map(Number); if (hh * 60 + mm <= nowMin) return false; }
      return true;
    };
    // Days from today through departure, but only those with at least one open slot.
    const start = new Date(); start.setHours(0, 0, 0, 0);
    const depDate = stay?.departure ? new Date(stay.departure + 'T00:00:00') : null;
    const closedDays = new Set(hmData?.closed?.[hmMeal] || []);
    const leadDays = Math.max(0, Number(hmData?.leadDays) || 0);   // admin lead-time — hide too-soon dates
    const hmDays = [];
    for (let i = leadDays; i < 21 + leadDays; i++) {
      const d = new Date(start); d.setDate(start.getDate() + i);
      if (depDate && d > depDate) break;
      const day = fmtDay(d, lang);
      if (closedDays.has(day.iso)) continue;   // admin-blacked-out date — hide
      if (slots.some(s => slotOpen(day.iso, s))) hmDays.push(day);
    }
    const selDay = (hmDate && hmDays.some(d => d.iso === hmDate)) ? hmDate : hmDays[0]?.iso;
    const daySlots = selDay ? slots.filter(s => slotOpen(selDay, s)) : [];
    return (
      <main className="idpage2 whenp hmwhen hm-decor" style={{ ...hmBg, '--brand': '#c25f74' }}>
        <HmPetals />
        <img className="steplogo" src={`/uploads/hm/logo-${hmMeal === 'dinner' ? 'dinner' : (({ 'Romance Breakfast': 'romance', 'Oriental Breakfast': 'oriental' })[hmGroup?.variant] || 'romance')}.png`} alt="" />
        <h1 className="whentitle">{t(lang, 'choose_datetime')}</h1>
        {hmDays.length === 0
          ? <p className="whenempty">{t(lang, 'no_dates')}</p>
          : (
            <div className="hm-whenbox">
              <div className="hm-daychips">
                {hmDays.map(d => (
                  <button key={d.iso} aria-pressed={selDay === d.iso} className={'hm-daychip' + (selDay === d.iso ? ' sel' : '')}
                    onClick={() => { setHmDate(d.iso); setHmSlot(null); }}>
                    <span className="hm-daychip-dow">{d.dow}</span>
                    <span className="hm-daychip-dom isonum">{nd(d.dom)}</span>
                    <span className="hm-daychip-mon">{d.mon}</span>
                  </button>
                ))}
              </div>
              <div className="hm-timerows">
                {Object.entries(daySlots.reduce((acc, s) => { const h = String(s.time).slice(0, 2); (acc[h] = acc[h] || []).push(s); return acc; }, {})).sort((a, b) => Number(a[0]) - Number(b[0])).map(([h, hslots]) => (
                  <div key={h} className="hm-timerow">
                    {hslots.map(s => (
                      <button key={s.id} aria-pressed={hmSlot === s.id} className={'hm-timeslot' + (hmSlot === s.id ? ' sel' : '')}
                        onClick={() => { setHmDate(selDay); setHmSlot(s.id); setError(''); }}>
                        <span className="isonum">{nd(s.time)}</span>
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          )}
        <button className="ri2back" onClick={() => setStep('hmenu')} aria-label={t(lang, 'back')}><ArrowLeft size={30} /></button>
        <button className="ri2next ri2arrow" disabled={hmSlot == null || !hmDate} onClick={() => { setError(''); setStep('hreview'); }}>{t(lang, 'next')}</button>
        {langBar}
        {errorModal}
      </main>
    );
  }

  if (step === 'hreview') {
    const rMenu = hmData?.menus?.[hmMeal] || {};
    const rVariant = rMenu.pickOne ? hmGroup : rMenu.variants?.[0];
    const rSlot = (hmData?.slots?.[hmMeal] || []).find(s => s.id === hmSlot);
    const rLoc = hmData?.locations?.[hmMeal];
    const grpShownR = (dc, grp) => !grp.showWhen || Object.values(dc || {}).some(arr => arr.some(o => o.en === grp.showWhen));
    const rLines = [];
    (rVariant?.sections || []).forEach(sec => (sec.dishes || []).forEach(dish => {
      const dc = hmChoices[dish.id]; if (!dc) return;
      const picks = [];
      (dish.optionGroups || []).forEach((grp, gi) => { if (grpShownR(dc, grp)) (dc[gi] || []).forEach(o => { const cur = grp.opts.find(x => x.en === o.en); if (cur) picks.push(cur.label); }); });
      if (picks.length) rLines.push({ dish: sec.name, picks: picks.join(lang === 'ar' ? '، ' : ', ') });
    }));
    return (
      <main className="idpage2 detp revp hmreview hm-decor" style={{ ...hmBg, '--brand': '#c25f74' }}>
        <HmPetals />
        <img className="steplogo" src={`/uploads/hm/logo-${hmMeal === 'dinner' ? 'dinner' : (({ 'Romance Breakfast': 'romance', 'Oriental Breakfast': 'oriental' })[hmGroup?.variant] || 'romance')}.png`} alt="" />
        <h1 className="whentitle">{t(lang, 'review_confirm')}</h1>
        <div className="revnote">{t(lang, 'review_note')}</div>
        <div className="detrows">
          {rMenu.pickOne && rVariant && <div className="detrow"><div className="detlabel"><UtensilsCrossed size={16} strokeWidth={1.9} /> {t(lang, 'menu')}</div><div className="detval">{rVariant.name}</div></div>}
          {rLoc && <div className="detrow"><div className="detlabel"><MapPin size={16} strokeWidth={1.9} /> {t(lang, 'hm_location')}</div><div className="detval">{rLoc}</div></div>}
          <div className="detrow"><div className="detlabel"><Clock3 size={16} strokeWidth={1.9} /> {t(lang, 'when')}</div><div className="detval"><span className="detwhen" dir={lang === 'ar' ? 'rtl' : 'ltr'}><b>{nd(domOf(hmDate))}</b> {monOf(hmDate)} · <span className="isonum">{nd(rSlot?.time)}</span></span></div></div>
          <div className="detrow"><div className="detlabel"><Users size={16} strokeWidth={1.9} /> {t(lang, 'table_for')}</div><div className="detval">{paxUnit(2)}</div></div>
          <div className="detrow revrow">
            <div className="detlabel"><UtensilsCrossed size={16} strokeWidth={1.9} /> {t(lang, 'your_order')}</div>
            <div className="revorder">
              {(rVariant?.sections || []).map(sec => (
                <div key={sec.id} className="revcat">
                  <div className="revcatname">{sec.name}</div>
                  {(sec.dishes || []).map(dish => {
                    const dc = hmChoices[dish.id] || {};
                    const picks = [];
                    (dish.optionGroups || []).forEach((grp, gi) => { if (grpShownR(dc, grp)) (dc[gi] || []).forEach(o => { const cur = grp.opts.find(x => x.en === o.en); if (cur) picks.push(cur.label); }); });
                    return <div key={dish.id} className="revitem"><span className="revname">{picks.length > 0 ? sec.name : dish.dish}</span>{picks.length > 0 && <span className="revpick"><Check size={13} strokeWidth={3} /> {picks.join(lang === 'ar' ? '، ' : ', ')}</span>}</div>;
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>
        <button className="ri2back" onClick={() => setStep('hwhen')} aria-label={t(lang, 'back')}><ArrowLeft size={30} /></button>
        <button className="ri2next ri2arrow" disabled={busy} onClick={hmBook}>{busy ? t(lang, 'reserving') : t(lang, 'reserve')}</button>
        {langBar}
        {errorModal}
      </main>
    );
  }

  if (step === 'hmdone') {
    const r = hmResult || {};
    const doneMenu = hmData?.menus?.[r.meal] || {};
    const doneVariant = doneMenu.pickOne ? hmGroup : doneMenu.variants?.[0];
    const grpShownDone = (dc, grp) => !grp.showWhen || Object.values(dc || {}).some(arr => arr.some(o => o.en === grp.showWhen));
    const choiceLines = [];
    (doneVariant?.sections || []).forEach(sec => (sec.dishes || []).forEach(dish => {
      const dc = hmChoices[dish.id];
      if (!dc) return;
      const picks = [];
      (dish.optionGroups || []).forEach((grp, gi) => { if (grpShownDone(dc, grp)) (dc[gi] || []).forEach(o => { const cur = grp.opts.find(x => x.en === o.en); if (cur) picks.push(cur.label); }); });
      if (picks.length) choiceLines.push({ dish: sec.name, picks: picks.join(lang === 'ar' ? '، ' : ', ') });
    }));
    return (
      <main className="idpage2 hmp hm-decor" style={hmBg}>
        <HmPetals />
        <img className="hm-reviewlogo" src={`/uploads/hm/logo-${r.meal === 'dinner' ? 'dinner' : (({ 'Romance Breakfast': 'romance', 'Oriental Breakfast': 'oriental' })[hmGroup?.variant] || 'romance')}.png`} alt="" />
        <div className="hm-bell"><Heart size={30} strokeWidth={1.5} fill="currentColor" aria-hidden="true" /></div>
        <h1 className="hm-title" style={{ fontStyle: 'normal', fontWeight: 600 }}>{t(lang, 'hm_reserved_title')}</h1>
        <span className="hm-accent" aria-hidden="true" />
        <p className="hm-sub">{t(lang, 'hm_reserved_sub')}</p>
        <div className="hm-summary">
          {doneMenu.pickOne && doneVariant && <div className="hm-line"><span>{t(lang, 'menu')}</span><b>{doneVariant.name}</b></div>}
          {(hmData?.locations?.[r.meal] || r.venue) && <div className="hm-line"><span>{t(lang, 'hm_location')}</span><b>{hmData?.locations?.[r.meal] || r.venue}</b></div>}
          <div className="hm-line"><span>{t(lang, 'when')}</span><b><span className="detwhen" dir={lang === 'ar' ? 'rtl' : 'ltr'}>{nd(domOf(r.date))} {monOf(r.date)} · <span className="isonum">{nd(r.time)}</span></span></b></div>
          <div className="hm-line"><span>{t(lang, 'table_for')}</span><b>{paxUnit(2)}</b></div>
          <div className="hm-line"><span>{t(lang, 'booking_ref')}</span><b>{r.ref}</b></div>
        </div>
        {choiceLines.length > 0 && (
          <div className="hm-choices">
            <div className="hm-choices-hd">{t(lang, 'hm_your_choices')}</div>
            {choiceLines.map((c, i) => (
              <div key={i} className="hm-choice"><span>{c.dish}</span><b><Check size={13} strokeWidth={3} /> {c.picks}</b></div>
            ))}
          </div>
        )}
        <button className="ri2back" onClick={() => setStep('honeymoon')} aria-label={t(lang, 'back')}><ArrowLeft size={30} /></button>
        <button className="ri2next ri2arrow" onClick={() => setStep('honeymoon')}>{t(lang, 'done')}</button>
        {langBar}
        {errorModal}
      </main>
    );
  }

  if (step === 'when') {
    // Step title is "Choose Date & Time" — "Reservation Details" belongs to the details step only.
    const bg = settings?.kiosk_bg_url || restaurant.image_url || '/img/gallery/ambience-1.webp';
    const idBg = { '--brand': (BRAND[restaurant.code] ?? '#2563eb'), backgroundImage: bg ? `linear-gradient(rgba(10,13,20,0.86), rgba(10,13,20,0.93)), url(${bg})` : (CARD_ART[restaurant.code] ?? CARD_ART.cedar) };
    return (
      <main className="idpage2 whenp" style={idBg}>
        <img className="steplogo" src={restLogo} alt={restaurant.names?.[lang] ?? restaurant.name}
             onError={e => { e.currentTarget.style.display = 'none'; }} />
        <h1 className="whentitle">{t(lang, 'choose_datetime')}</h1>
        <div className="whenrow">
          <div className="whenlabel">{t(lang, 'when')}</div>
          {days.length === 0 && <div className="whenempty">{t(lang, 'no_dates')}</div>}
          <div className="whencols">
            {days.map(d => {
              const slots = slotsByDay[d.iso] ?? [];
              return (
                <div key={d.iso} className="whencol">
                  <div className="whendow">{d.dow}</div>
                  <div className="whendom">{nd(d.dom)}</div>
                  <div className="whenmon">{d.mon}</div>
                  <div className="whenslots">
                    {slots.length === 0 && <div className="whennoavail">{t(lang, 'err_slot_closed')}</div>}
                    {slots.map(s => (
                      <button key={`${s.timingId}-${s.time}`}
                        className={'whenslot' + (when?.date === d.iso && when?.time === s.time && when?.timingId === s.timingId ? ' sel' : '') + (s.remaining <= 0 ? ' full' : '')}
                        disabled={s.remaining <= 0}
                        onClick={() => { setWhen({ date: d.iso, time: s.time, timingId: s.timingId, area: s.name, paid: s.paid, ...d }); setStep('details'); }}>
                        {s.remaining <= 0 ? <span className="whenfull">{t(lang, 'err_slot_closed')}</span> : nd(s.time)}
                        {s.remaining > 0 && (() => { const label = mealLabel(s.name, lang); return label ? <span className="whenarea">{label}</span> : null; })()}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        {/* Charge note sits BELOW the date/time picker (on mobile it flows here,
            under the cards; on the kiosk it's absolute-positioned at the bottom
            regardless of DOM order). */}
        {quote?.chargeType === 'chargeable' && (
          <div className="whencharge">
            <b>{t(lang, 'extra_charge')}</b><span className="chargesep">&mdash;</span>{settings?.menu_choice_enabled === 'yes'
              ? t(lang, 'pay_at_restaurant')
              : quote.chargeAmount > 0
                ? t(lang, 'extra_charge_note').replace('{amount}', `${nd(quote.chargeAmount)} ${quote.currency}`.trim())
                : t(lang, 'pay_at_restaurant')}
          </div>
        )}
        <button className="ri2back" onClick={() => setStep('welcome')} aria-label={t(lang, 'back')}><ArrowLeft size={30} /></button>
        {langBar}
      </main>
    );
  }


  if (step === 'details') {
    const bg = settings?.kiosk_bg_url || restaurant.image_url || '/img/gallery/ambience-1.webp';
    const idBg = { '--brand': (BRAND[restaurant.code] ?? '#2563eb'), backgroundImage: bg ? `linear-gradient(rgba(10,13,20,0.86), rgba(10,13,20,0.93)), url(${bg})` : (CARD_ART[restaurant.code] ?? CARD_ART.cedar) };
    const langMap = { en: 'fr', it: 'fr' };
    const full = menuPdfs.find(p => p.kind === 'full' && p.lang === (langMap[lang] ?? lang))
              ?? menuPdfs.find(p => p.kind === 'full' && p.lang === 'fr')
              ?? menuPdfs.find(p => p.kind === 'full');
    return (
      <main className="idpage2 detp" style={idBg}>
        <img className="steplogo" src={restLogo} alt={restaurant.names?.[lang] ?? restaurant.name} onError={e => { e.currentTarget.style.display = 'none'; }} />
        <h1 className="whentitle">{t(lang, 'booking_details')}</h1>

        <div className="detrows">
          {/* When */}
          <div className="detrow">
            <div className="detlabel">{t(lang, 'when')}</div>
            <div className="detval">
              <span className="detwhen" dir={lang === 'ar' ? 'rtl' : 'ltr'}><b>{nd(domOf(when.date))}</b> {monOf(when.date)} {dtSep} <span className="isonum">{nd(when.time)}</span></span>
              <button className="detchange" onClick={() => setStep('when')}><Pencil size={16} /> {t(lang, 'change')}</button>
            </div>
          </div>

          {/* Table for */}
          <div className="detrow">
            <div className="detlabel">{t(lang, 'table_for')}</div>
            <div className="detval">
              {appSettings?.guest_select_pax === 'yes' ? (() => {
                const cap = restaurant?.max_pax > 0
                  ? Math.min(Number(settings?.max_connected_pax || 8), restaurant.max_pax)
                  : Number(settings?.max_connected_pax || 8);
                return (
                  <div className="paxstep">
                    <button className="paxbtn" aria-label={t(lang, 'fewer')} disabled={pax <= 1} onClick={() => setPax(p => Math.max(1, p - 1))}><Minus size={20} /></button>
                    <span className="detpax">{nd(pax)}</span>
                    <button className="paxbtn" aria-label={t(lang, 'more')} disabled={pax >= cap} onClick={() => setPax(p => Math.min(cap, p + 1))}><Plus size={20} /></button>
                  </div>
                );
              })() : (
                <span className="detpax">{paxUnit(pax)}</span>
              )}
            </div>
          </div>

          {/* Special occasion */}
          <div className="detrow">
            <div className="detlabel">{t(lang, 'special_occasion')}</div>
            <div className="detchips">
              {OCCASIONS.map(o => (
                <button key={o.id} className={'detchip' + (occasion === o.id ? ' sel' : '')}
                  onClick={() => setOccasion(occasion === o.id ? null : o.id)}>
                  <o.icon size={34} strokeWidth={1.5} /><span>{t(lang, o.id)}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Diet */}
          <div className="detrow">
            <div className="detlabel">{t(lang, 'diet')}</div>
            <div>
              <div className="detchips">
                <button className={'detchip' + (dietOpen === 'allergies' || allergies.length ? ' sel' : '')}
                  onClick={() => setDietOpen(dietOpen === 'allergies' ? null : 'allergies')}>
                  <Wheat size={34} strokeWidth={1.5} /><span>{t(lang, 'allergies')}{allergies.length ? ` (${allergies.length})` : ''}</span>
                </button>
                <button className={'detchip' + (dietOpen === 'dietary' || dietary.length ? ' sel' : '')}
                  onClick={() => setDietOpen(dietOpen === 'dietary' ? null : 'dietary')}>
                  <Shell size={34} strokeWidth={1.5} /><span>{t(lang, 'dietary')}{dietary.length ? ` (${dietary.length})` : ''}</span>
                </button>
              </div>
              {dietOpen === 'allergies' && (
                <div className="detchips small" style={{ marginTop: 12 }}>
                  {ALLERGIES.map(a => { const Icon = ALLERGY_ICONS[a]; return (
                    <button key={a} className={'detchip sm' + (allergies.includes(a) ? ' sel' : '')} onClick={() => toggle(allergies, setAllergies, a)}>
                      <Icon size={24} strokeWidth={1.5} /><span>{t(lang, a)}</span></button>); })}
                </div>
              )}
              {dietOpen === 'dietary' && (
                <div className="detchips small" style={{ marginTop: 12 }}>
                  {DIETARY.map(d => { const Icon = DIETARY_ICONS[d]; return (
                    <button key={d} className={'detchip sm' + (dietary.includes(d) ? ' sel' : '')} onClick={() => toggle(dietary, setDietary, d)}>
                      <Icon size={24} strokeWidth={1.5} /><span>{t(lang, d)}</span></button>); })}
                </div>
              )}
            </div>
          </div>
        </div>

        {quote?.chargeType === 'chargeable' && (
          <div className="whencharge">
            <b>{t(lang, 'extra_charge')}</b><span className="chargesep">&mdash;</span>{settings?.menu_choice_enabled === 'yes'
              ? t(lang, 'pay_at_restaurant')
              : quote.chargeAmount > 0
                ? t(lang, 'extra_charge_note').replace('{amount}', `${nd(quote.chargeAmount)} ${quote.currency}`.trim())
                : t(lang, 'pay_at_restaurant')}
          </div>
        )}

        <button className="ri2back" onClick={() => setStep('when')} aria-label={t(lang, 'back')}><ArrowLeft size={30} /></button>
        <button className="ri2next ri2arrow" disabled={busy} onClick={() => {
          const hasMenu = (menu ?? []).some(c => c.items.length);
          if (hasMenu && settings?.menu_choice_enabled === 'yes') { setError(''); setStep('menu'); }
          else reserve();
        }}>
          {busy ? t(lang, 'reserving') : t(lang, 'reserve')}
          {settings?.menu_choice_enabled !== 'yes' && quote?.chargeType === 'chargeable' && quote.chargeAmount > 0 && ` · ${nd(quote.chargeAmount)} ${quote.currency}`.trimEnd()}
        </button>
        {langBar}
        {errorModal}
        {pdfOpen && (
          <div className="pdfmask" onClick={() => setPdfOpen(null)}>
            <div className="pdfframe" onClick={e => e.stopPropagation()}>
              <button className="pdfclose" onClick={() => setPdfOpen(null)} aria-label={t(lang, 'close')}><X size={22} /></button>
              <iframe src={pdfOpen} title="menu" />
            </div>
          </div>
        )}
      </main>
    );
  }

  if (step === 'menu') {
    const total = (menu ?? []).flatMap(c => c.items).reduce((s, i) => s + (dishes[i.id] ?? 0) * Number(i.price), 0);
    const count = Object.values(dishes).reduce((s, q) => s + (q || 0), 0);
    const cur = curOf(quote?.currency ?? restaurant?.currency ?? '');
    // Prices are shown only when the board is actually charged (à la carte / not included).
    // All-inclusive / free boards (e.g. AI) don't show per-dish prices — they're not billed per item.
    const chargeable = quote?.chargeType === 'chargeable';
    const showPrices = chargeable;
    const bg = settings?.kiosk_bg_url || restaurant.image_url || '/img/gallery/ambience-1.webp';
    const menuBg = { '--brand': (BRAND[restaurant.code] ?? '#2563eb'), backgroundImage: `linear-gradient(rgba(10,13,20,0.88), rgba(10,13,20,0.94)), url(${bg})`, backgroundSize: 'cover', backgroundPosition: 'center' };
    return (
      <main className="idpage2 menup" style={menuBg}>
        <div className="menuhead">
          <div className="menuhero">
            <span className="menukicker"><ChefHat size={16} strokeWidth={1.75} /> {t(lang, 'menu')}</span>
            <img className="menulogo" src={restLogo} alt={restaurant.names?.[lang] ?? restaurant.name}
                 onError={e => { e.currentTarget.style.display = 'none'; e.currentTarget.nextElementSibling.style.display = 'block'; }} />
            <h1 className="menuheroname" style={{ display: 'none' }}>{restaurant.names?.[lang] ?? restaurant.name}</h1>
            <span className="menusub">{t(lang, 'choose_dishes')}</span>
            {quote?.boardName && (
              <span className={'boardbadge' + (chargeable ? ' pay' : '')}>
                {quote.boardName} · {t(lang, chargeable ? 'board_charged' : 'board_included')}
              </span>
            )}
          </div>
        </div>
        <div className="menuscroll">
          {(menu ?? []).filter(c => c.items.length).map(c => (
            <section key={c.id} className="mcat">
              <h2 className="mcathd">{(() => { const Ic = catIcon(c.name); return <Ic className="mcaticon" size={18} strokeWidth={1.75} />; })()}{c.name}</h2>
              <div className="mitems">
                {c.items.map(i => {
                  const q = dishes[i.id] ?? 0;
                  return (
                    <div key={i.id} className={'mitem' + (q > 0 ? ' on' : '')}>
                      {i.photo_url
                        ? <img className="mi-photo" src={i.photo_url} alt="" loading="lazy" onError={e => { e.currentTarget.style.visibility = 'hidden'; }} />
                        : <span className="mi-photo ph"><UtensilsCrossed size={20} strokeWidth={1.5} /></span>}
                      <div className="mi-info">
                        <div className="mi-name">{i.name}</div>
                        {i.description && <div className="mi-desc">{i.description}</div>}
                      </div>
                      {Number(i.price) > 0 && showPrices && <div className="mi-price">{nd(Number(i.price))} <span>{cur}</span></div>}
                      <div className="mi-qty">
                        {q > 0 && <button className="mi-minus" onClick={() => setDishes(d => ({ ...d, [i.id]: Math.max(0, (d[i.id] ?? 0) - 1) }))}><Minus size={18} /></button>}
                        {q > 0 && <span className="mi-count">{nd(q)}</span>}
                        <button className="mi-plus" onClick={() => setDishes(d => ({ ...d, [i.id]: Math.min(20, (d[i.id] ?? 0) + 1) }))}><Plus size={18} /></button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
        {langBar}
        {errorModal}
        <div className="menubar">
          <button className="ri2back menustatic" onClick={() => setStep('details')} aria-label={t(lang, 'back')}><ArrowLeft size={30} /></button>
          {count > 0 && <span className="menutotal"><span className="mtpart">{itemsLbl} {nd(count)}</span>{showPrices && total > 0 ? <span className="mtpart mttotal">{t(lang, 'total')} {nd(total)} {cur}</span> : null}</span>}
          <div className="menubarright">
            {settings?.menu_choice_forced !== 'yes' && count === 0 && (
              <button className="menuskip" onClick={() => { setDishes({}); reserve(); }}>{t(lang, 'skip')}</button>
            )}
            <button className="ri2next ri2arrow menustatic" disabled={busy || count === 0} onClick={() => { setError(''); setStep('review'); }}>
              {t(lang, 'next')}{chargeable && total > 0 ? ` · ${nd(total)} ${cur}` : ''}
            </button>
          </div>
        </div>
      </main>
    );
  }

  if (step === 'review') {
    const items = (menu ?? []).flatMap(c => c.items).filter(i => (dishes[i.id] ?? 0) > 0);
    const total = items.reduce((s, i) => s + (dishes[i.id] ?? 0) * Number(i.price), 0);
    const cur = curOf(quote?.currency ?? restaurant?.currency ?? '');
    const chargeable = quote?.chargeType === 'chargeable';
    const showPrices = chargeable;
    const bg = settings?.kiosk_bg_url || restaurant.image_url || '/img/gallery/ambience-1.webp';
    const idBg = { '--brand': (BRAND[restaurant.code] ?? '#2563eb'), backgroundImage: `linear-gradient(rgba(10,13,20,0.86), rgba(10,13,20,0.93)), url(${bg})` };
    return (
      <main className="idpage2 detp revp" style={idBg}>
        <img className="steplogo" src={restLogo} alt={restaurant.names?.[lang] ?? restaurant.name} onError={e => { e.currentTarget.style.display = 'none'; }} />
        <h1 className="whentitle">{t(lang, 'review_confirm')}</h1>
        <div className="detrows">
          <div className="detrow">
            <div className="detlabel">{t(lang, 'when')}</div>
            <div className="detval"><span className="detwhen" dir={lang === 'ar' ? 'rtl' : 'ltr'}><b>{nd(domOf(when.date))}</b> {monOf(when.date)} {dtSep} <span className="isonum">{nd(when.time)}</span></span></div>
          </div>
          <div className="detrow">
            <div className="detlabel">{t(lang, 'table_for')}</div>
            <div className="detval"><span className="detpax">{paxUnit(pax)}</span></div>
          </div>
          {occasion && (
            <div className="detrow">
              <div className="detlabel">{t(lang, 'special_occasion')}</div>
              <div className="detval">{t(lang, occasion)}</div>
            </div>
          )}
          {allergies.length > 0 && (
            <div className="detrow">
              <div className="detlabel">{t(lang, 'allergies')}</div>
              <div className="detval">{allergies.map(a => t(lang, a)).join(lang === 'ar' ? '، ' : ', ')}</div>
            </div>
          )}
          {dietary.length > 0 && (
            <div className="detrow">
              <div className="detlabel">{t(lang, 'dietary')}</div>
              <div className="detval">{dietary.map(d => t(lang, d)).join(lang === 'ar' ? '، ' : ', ')}</div>
            </div>
          )}
          {items.length > 0 && (
            <div className="detrow revrow">
              <div className="detlabel">{t(lang, 'your_order')}</div>
              <div className="revorder">
                {(menu ?? []).map(c => {
                  const its = c.items.filter(i => (dishes[i.id] ?? 0) > 0);
                  if (!its.length) return null;
                  return (
                    <div key={c.id} className="revcat">
                      <div className="revcatname">{c.name}</div>
                      {its.map(i => (
                        <div key={i.id} className="revitem">
                          <div className="revstep">
                            <button className="revstepbtn" aria-label={t(lang, 'fewer')} onClick={() => setDishes(d => ({ ...d, [i.id]: Math.max(0, (d[i.id] ?? 0) - 1) }))}><Minus size={16} strokeWidth={2.5} /></button>
                            <span className="revqty">{nd(dishes[i.id])}</span>
                            <button className="revstepbtn" aria-label={t(lang, 'more')} onClick={() => setDishes(d => ({ ...d, [i.id]: Math.min(20, (d[i.id] ?? 0) + 1) }))}><Plus size={16} strokeWidth={2.5} /></button>
                          </div>
                          <span className="revname">{i.name}</span>
                          {Number(i.price) > 0 && showPrices && <span className="revprice">{nd(dishes[i.id] * Number(i.price))} {cur}</span>}
                        </div>
                      ))}
                    </div>
                  );
                })}
                {showPrices && total > 0 && <div className="revtotalbar"><span>{t(lang, 'total')}</span><b>{nd(total)} {cur}</b></div>}
              </div>
            </div>
          )}
        </div>
        <div className="revnote">{t(lang, 'review_note')}</div>
        <button className="ri2back" onClick={() => { setError(''); setStep('menu'); }} aria-label={t(lang, 'back')}><ArrowLeft size={30} /></button>
        <button className="ri2next" disabled={busy} onClick={reserve}>
          {busy ? t(lang, 'reserving') : t(lang, 'confirm')}{chargeable && total > 0 ? ` · ${nd(total)} ${cur}` : ''}
        </button>
        {langBar}
        {errorModal}
      </main>
    );
  }

  if (step === 'intro') {
    const bg = settings?.kiosk_bg_url || restaurant.image_url || '/img/gallery/ambience-1.webp';
    const desc = restaurant.descs?.[lang] || restaurant.descs?.en;
    const gf = menuPdfs.find(p => p.kind === 'glutenfree');
    const langMap = { en: 'fr', it: 'fr' };
    const full = menuPdfs.find(p => p.kind === 'full' && p.lang === (langMap[lang] ?? lang))
              ?? menuPdfs.find(p => p.kind === 'full' && p.lang === 'fr')
              ?? menuPdfs.find(p => p.kind === 'full');
    // Kiosk shows exactly the photos configured in admin → restaurant Photos.
    // No bundled stock fallback: if a manager removes them all, the page falls
    // back to the clean centered hero rather than phantom stock images.
    const gallery = restaurant.photos ?? [];
    return (
      <main className={'rintro2' + (gallery.length ? '' : ' nogallery')} style={{ '--brand': (BRAND[restaurant.code] ?? '#2563eb'), backgroundImage: bg ? `linear-gradient(rgba(10,13,20,0.86), rgba(10,13,20,0.93)), url(${bg})` : (CARD_ART[restaurant.code] ?? CARD_ART.cedar) }}>
        {gallery.length > 0 && (
          <div className="ri2gallery" data-count={Math.min(gallery.length, 5)}>
            {gallery.slice(0, 5).map((src, i) => (
              <img key={i} className="ri2photo" src={src} alt=""
                onError={e => { e.currentTarget.style.display = 'none'; }} />
            ))}
          </div>
        )}

        <div className="ri2left">
          {/* The branded logo IS the title — sized uniformly across restaurants.
              Falls back to the plain name if the logo image is missing. */}
          <img className="ri2logo" src={restLogo} alt={restaurant.names?.[lang] ?? restaurant.name}
            onError={e => { e.currentTarget.style.display = 'none'; const h = e.currentTarget.nextElementSibling; if (h) h.style.display = 'block'; }} />
          <h1 className="ri2name" style={{ display: 'none' }}>{restaurant.names?.[lang] ?? restaurant.name}</h1>
          <span className="ri2accent" aria-hidden="true" />
          {desc && <p className="ri2desc">{desc}</p>}
          {/* The additional-charge badge already appears on the restaurant card
             (cover) in the grid, so it's redundant here on the intro page —
             removed to avoid showing it twice for always-charge outlets (Reefs). */}
        </div>

        <button className="ri2back" onClick={() => setStep('restaurants')} aria-label={t(lang, 'back')}><ArrowLeft size={30} /></button>
        <button className="ri2next ri2arrow" onClick={() => { setError(''); setStep('identity'); }}>
          {t(lang, 'next')}
        </button>

        {pdfOpen && (
          <div className="pdfmask" onClick={() => setPdfOpen(null)}>
            <div className="pdfframe" onClick={e => e.stopPropagation()}>
              <button className="pdfclose" onClick={() => setPdfOpen(null)} aria-label={t(lang, 'close')}><X size={22} /></button>
              <iframe src={pdfOpen} title="menu" />
            </div>
          </div>
        )}
      </main>
    );
  }


  if (step === 'identity') {
    const bg = settings?.kiosk_bg_url || restaurant.image_url || '/img/gallery/ambience-1.webp';
    return (
      <main className="idpage2" style={{ backgroundImage: bg ? `linear-gradient(rgba(10,13,20,0.86), rgba(10,13,20,0.93)), url(${bg})` : (CARD_ART[restaurant.code] ?? CARD_ART.cedar) }}>
        <img className="steplogo" src={restLogo} alt={restaurant.names?.[lang] ?? restaurant.name} onError={e => { e.currentTarget.style.display = 'none'; }} />
        <div className="id2left">
          <h1 className="id2title">{t(lang, 'enter_details')}</h1>

          <div className="id2fields">
            <div className="id2field">
              <div className="id2label">{t(lang, 'room_number')}</div>
              <input className="id2box" inputMode="numeric" autoComplete="off"
                value={nd(room)} dir="ltr"
                onChange={e => setRoom(denum(e.target.value).slice(0, 6))} />
            </div>

            <div className="id2field">
              <div className="id2label">{t(lang, 'departure_date')}</div>
              <button className="id2box asbtn" onClick={() => setCalOpen(o => !o)}>
                {departure ? <span className="isonum">{nd(departure)}</span> : t(lang, 'select_date')}
              </button>
              {calOpen && (
                <div className="calwrap id2cal">
                  <DayPicker mode="single"
                    selected={departure ? new Date(departure + 'T00:00:00') : undefined}
                    onSelect={d => { setDeparture(toISO(d)); setCalOpen(false); }}
                    disabled={{ before: new Date() }}
                    formatters={{ formatWeekdayName: d => lang === 'ar' ? AR_WEEKDAYS_SHORT[d.getDay()] : d.toLocaleDateString(lang, { weekday: 'short' }), formatDay: d => nd(d.getDate()), formatCaption: d => `${lang === 'ar' ? AR_MONTHS[d.getMonth()] : d.toLocaleDateString(lang, { month: 'long' })} ${nd(d.getFullYear())}` }} />
                </div>
              )}
            </div>
          </div>

        </div>

        <div className="id2keypad">
          {['1','2','3','4','5','6','7','8','9'].map(k => (
            <button key={k} onClick={() => setRoom(r => (r + k).slice(0, 6))}>{nd(k)}</button>
          ))}
          <span />
          <button onClick={() => setRoom(r => (r + '0').slice(0, 6))}>{nd('0')}</button>
          <button onClick={() => setRoom(r => r.slice(0, -1))} aria-label={t(lang, 'backspace')}><ChevronLeft size={44} strokeWidth={2.75} /></button>
        </div>

        {!calOpen && <button className="ri2back" onClick={() => { setError(''); setStep('restaurants'); }} aria-label={t(lang, 'back')}><ArrowLeft size={30} /></button>}
        <button className="ri2next ri2arrow" disabled={busy || !room || !departure} onClick={verifyGuest}>
          {busy ? t(lang, 'checking') : t(lang, 'next')}
        </button>

        {busy && (
          <div className="verifyloading" aria-live="polite" aria-busy="true">
            <span className="wlmspin" />
            <span className="verifyloadingtxt">{t(lang, 'checking')}</span>
          </div>
        )}
        {errorModal}
      </main>
    );
  }

  // --- confirmation (branded, matches the rest of the flow) ---
  const doneBg = settings?.kiosk_bg_url || restaurant?.image_url || '/img/gallery/ambience-1.webp';
  const doneStyle = {
    '--brand': (BRAND[restaurant?.code] ?? '#2563eb'),
    backgroundImage: doneBg
      ? `linear-gradient(rgba(10,13,20,0.86), rgba(10,13,20,0.93)), url(${doneBg})`
      : (CARD_ART[restaurant?.code] ?? CARD_ART.cedar),
  };
  const doneCurrency = curOf(restaurant?.currency ?? quote?.currency ?? '');
  const doneAmount = result.chargeAmount > 0 ? `${nd(result.chargeAmount)} ${doneCurrency}`.trim() : '';
  const guestName = stay?.display_name || stay?.guest_name || '';
  return (
    <main className="confirm2" style={doneStyle}>
      <div className="c2card">
        <div className="c2check"><Check size={42} strokeWidth={2.25} /></div>
        <div className="c2title">{t(lang, 'confirmed')}</div>
        {guestName && <div className="c2guest">{guestName}</div>}

        <div className="c2rows">
          <div className="c2row"><span><UtensilsCrossed size={15} strokeWidth={1.75} /> {t(lang, 'restaurant')}</span><b>{restaurant?.names?.[lang] ?? result.restaurant}</b></div>
          {stay?.room_number && <div className="c2row"><span><BedDouble size={15} strokeWidth={1.75} /> {t(lang, 'room')}</span><b dir="ltr">{nd(stay.room_number)}</b></div>}
          <div className="c2row"><span><CalendarDays size={15} strokeWidth={1.75} /> {t(lang, 'when')}</span><b dir="ltr">{nd(domOf(when.date))} {monOf(when.date)} {dtSep} {nd(result.time)}</b></div>
          <div className="c2row"><span><Users size={15} strokeWidth={1.75} /> {t(lang, 'table_for')}</span><b>{paxUnit(result.pax)}</b></div>
          {companions.length > 0 && <div className="c2row"><span><Users size={15} strokeWidth={1.75} /> {t(lang, 'shared_with')}</span><b>{companions.map(c => c.room).join(' · ')}</b></div>}
          {result.chargeType === 'chargeable' && <div className="c2row charge"><span><Sparkles size={15} strokeWidth={1.75} /> {t(lang, 'extra_charge')}</span><b>{doneAmount || t(lang, 'chargeable')}</b></div>}
        </div>

        <div className="c2note"><Info size={15} /> {t(lang, 'manage_note')}</div>
        <div className="c2ref"><Hash size={13} strokeWidth={2} /> {t(lang, 'booking_ref')}{lang === 'ar' ? ': ' : ' '}<b dir={lang === 'ar' ? undefined : 'ltr'}>{lang === 'ar' ? '' : '#'}{nd(result.id)}</b></div>

        <div className="c2actions">
          <button className="c2new" onClick={() => { reset(); setStep('restaurants'); }}>{t(lang, 'new_booking')}</button>
          <button className="c2done" onClick={reset}>{t(lang, 'done')}</button>
        </div>
      </div>
    </main>
  );
}
